import type {
  AccountingInvoiceJson,
  BiAllowancePreview,
  CashApplicationPreview,
  CreditMemoPreviewLine,
  InvoiceAssignmentLine,
  LedgerAccountJson,
  QbCustomer,
  Split,
  TransactionJson,
} from "@/api/accounting.tsx"
import { score_customer_for_deduction_source } from "@/accounting/accounting_check_match.ts"
import { exactCurrencyFormatter } from "@/utils"
import { parseISO } from "date-fns"

/** Deduction line context used to rank accounting invoices. */
export interface InvoiceRecommendationContext {
  invoice_number: string
  amount_dollars: number
  /** The line's remitted PO, matched against the invoice's reference. */
  po_number?: string | null
}

export interface CreditMemoSplitContributor {
  deduction_id: string
  invoice_ref: string
  kind: string
  amount: number
  memo?: string
  gl_account_number?: string
}

export function normalize_invoice_number_for_match(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "")
}

export function invoice_number_alphanumeric_only(raw: string): string {
  return raw.replace(/[^a-z0-9]/gi, "").toLowerCase()
}

/**
 * `parseISO` is the single most expensive step in scoring an invoice, and the apply page scores
 * every invoice in the org once per line on the check. Invoice dates repeat heavily (one distinct
 * string per calendar day), so memoizing the parse keeps the cache tiny and the hit rate near 100%.
 * Returns 0 for a missing or unparseable date, which is how the ordering treated it already.
 */
const invoice_date_ms_cache = new Map<string, number>()
export function invoice_date_ms(raw: string | null | undefined): number {
  if (!raw) {
    return 0
  }
  const cached = invoice_date_ms_cache.get(raw)
  if (cached !== undefined) {
    return cached
  }
  let ms = 0
  try {
    const t = parseISO(raw).getTime()
    ms = Number.isNaN(t) ? 0 : t
  } catch {
    ms = 0
  }
  invoice_date_ms_cache.set(raw, ms)
  return ms
}

/** Leading accounting doc-type token (e.g. Xero's "SI ", "INV ", "BILL ", "PO ") separated from the
 * real invoice number by whitespace. Requires whitespace so it never strips part of the number
 * itself (e.g. the "US" in "US-001234", which is followed by a hyphen, is kept). */
const DOCTYPE_PREFIX = /^[a-z]{1,4}\s+/i

/** Alphanumeric core of an invoice number after dropping a leading doc-type prefix token. */
export function invoice_core_without_doctype_prefix(raw: string): string {
  return invoice_number_alphanumeric_only(raw.trim().replace(DOCTYPE_PREFIX, ""))
}

/**
 * True when two invoice numbers match except for a leading accounting doc-type prefix — e.g. our
 * `US-001234` vs Xero's `SI US-001234`. Treated as a formatting-only difference (not a fuzzy/typo
 * match), so it ranks and auto-assigns like an exact match. Conservative: the matching core must be
 * ≥ 6 chars so short ids aren't linked, and the bare cores must differ (exact matches are a higher
 * tier handled elsewhere).
 */
export function invoice_prefix_only_match(a: string, b: string): boolean {
  const aCore = invoice_number_alphanumeric_only(a)
  const bCore = invoice_number_alphanumeric_only(b)
  if (aCore.length === 0 || bCore.length === 0 || aCore === bCore) {
    return false
  }
  const aStripped = invoice_core_without_doctype_prefix(a)
  const bStripped = invoice_core_without_doctype_prefix(b)
  const minLen = 6
  const pairs: [string, string][] = [
    [aStripped, bCore],
    [aCore, bStripped],
    [aStripped, bStripped],
  ]
  return pairs.some(([x, y]) => x.length >= minLen && y.length >= minLen && x === y)
}

/** UNFI's per-line `-<n>-East` / `-<n>-West` suffix on a portal invoice line (`INV33141-0-East`). */
const UNFI_LINE_SUFFIX = /-\d+-[a-z]+$/i

/** Leading alphabetic doc-type prefix glued straight onto the digits (`INV33259`, `SI00003888`). */
const GLUED_DOCTYPE_PREFIX = /^[a-z]{1,4}(?=\d)/i

/** Alphanumeric core of an invoice number with UNFI's line suffix dropped. */
export function invoice_core_without_line_suffix(raw: string): string {
  let s = raw.trim()
  while (UNFI_LINE_SUFFIX.test(s)) {
    s = s.replace(UNFI_LINE_SUFFIX, "")
  }
  return invoice_number_alphanumeric_only(s)
}

/**
 * True when two invoice numbers are the same number under presentation differences the accounting
 * system and the remittance disagree on: a doc-type prefix glued onto the digits (Walmart remits
 * `33259`, NetSuite calls it `INV33259`) and/or UNFI's per-line `-0-East` suffix (`INV33141-0-East`
 * vs `INV33141`). Formatting-only, so it ranks and auto-assigns like an exact match regardless of
 * amount — the early-pay discount makes the amounts legitimately differ, which is exactly the case
 * the amount-gated substring rule below missed for every discounted Walmart invoice. The shared core
 * must be at least 4 characters and the bare cores must differ (exact matches are a higher tier).
 */
export function invoice_bare_number_match(a: string, b: string): boolean {
  const aCore = invoice_number_alphanumeric_only(a)
  const bCore = invoice_number_alphanumeric_only(b)
  if (aCore.length === 0 || bCore.length === 0 || aCore === bCore) {
    return false
  }
  const aBase = invoice_core_without_line_suffix(a)
  const bBase = invoice_core_without_line_suffix(b)
  const aBare = aBase.replace(GLUED_DOCTYPE_PREFIX, "")
  const bBare = bBase.replace(GLUED_DOCTYPE_PREFIX, "")
  const minLen = 4
  const pairs: [string, string][] = [
    [aBase, bBase],
    [aBare, bBase],
    [aBase, bBare],
    [aBare, bBare],
  ]
  return pairs.some(([x, y]) => x.length >= minLen && y.length >= minLen && x === y)
}

function dollars_to_cents(d: number): number {
  return Math.round(d * 100 + Number.EPSILON)
}

/** Levenshtein distance for short invoice tokens only (caller caps length). */
function levenshtein_distance(a: string, b: string): number {
  const m = a.length
  const n = b.length
  if (m === 0) {
    return n
  }
  if (n === 0) {
    return m
  }
  let prev = new Array<number>(n + 1)
  let curr = new Array<number>(n + 1)
  for (let j = 0; j <= n; j++) {
    prev[j] = j
  }
  for (let i = 1; i <= m; i++) {
    curr[0] = i
    const ca = a.charCodeAt(i - 1)
    for (let j = 1; j <= n; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[n]
}

/**
 * Conservative fuzzy invoice-number check on **alphanumeric cores** (see `invoice_number_alphanumeric_only`).
 * - Exact core equality is handled elsewhere; distance 0 returns false here.
 * - Requires core length ≥ 6 so tiny IDs are not auto-linked on typos.
 * - Allows a single insert/delete/substitution (Levenshtein ≤ 1), which covers one wrong digit
 *   or a missing/extra digit in the numeric tail; not aggressive (no transposition-specific loosening).
 */
export function invoice_numbers_fuzzy_close(a: string, b: string): boolean {
  const a0 = invoice_number_alphanumeric_only(a)
  const b0 = invoice_number_alphanumeric_only(b)
  const minLen = 6
  if (a0.length < minLen || b0.length < minLen) {
    return false
  }
  if (a0 === b0) {
    return false
  }
  const maxCore = 40
  if (a0.length > maxCore || b0.length > maxCore) {
    return false
  }
  if (Math.abs(a0.length - b0.length) > 1) {
    return false
  }
  return levenshtein_distance(a0, b0) === 1
}

/**
 * Tight band for auto-assign when invoice numbers are only fuzzy-close: min($1, 1% of line amount)
 * in cents (same cents rounding as elsewhere). Zero line amount: only exact cents match qualifies elsewhere.
 */
export function amounts_close_for_auto_assign(want_dollars: number, have_dollars: number): boolean {
  const wantCents = dollars_to_cents(want_dollars)
  const haveCents = dollars_to_cents(have_dollars)
  const diff = Math.abs(wantCents - haveCents)
  const onePercentCents = Math.round(Math.abs(wantCents) * 0.01)
  const capCents = Math.min(100, onePercentCents)
  return diff <= capCents
}

/**
 * The key a PO number and an invoice reference are compared on: digits only, leading zeros dropped
 * (KeHE remits PO `03876078`; the Xero reference reads `3876078`). `""` under five significant
 * digits, too short to identify one order. Mirrors `db::accounting::po_match_key` on the server.
 */
export function po_match_key(raw: string | null | undefined): string {
  const key = (raw ?? "").replace(/\D/g, "").replace(/^0+/, "")
  return key.length < 5 ? "" : key
}

/**
 * True when a remitted invoice number is really the customer's PO wearing an invoice prefix. KeHE
 * sometimes keys its own PO where the brand's invoice number belongs — Compostic's check 1391147
 * paid `SI3876078` (PO `03876078`) for Xero's `SI-00004155`. Such a number names no accounting
 * invoice, so it must not be matched on at all; the PO is the only reliable link.
 */
export function invoice_number_is_po_placeholder(invoice_number: string, po_number: string | null | undefined): boolean {
  const po = po_match_key(po_number)
  if (!po) {
    return false
  }
  return invoice_number.replace(/\D/g, "").replace(/^0+/, "") === po
}

/** True when the invoice's reference names the line's PO (see `po_match_key`). */
export function invoice_reference_matches_po(
  inv: Pick<AccountingInvoiceJson, "reference">,
  po_number: string | null | undefined
): boolean {
  const po = po_match_key(po_number)
  return po !== "" && po_match_key(inv.reference) === po
}

/**
 * Amount band for a PO-only match: what the check settles within 5% of the invoice total. A PO
 * match carries no invoice-number evidence, so the amount must corroborate it; 5% is the same
 * ceiling the server puts on an additional discount, and clears KeHE's 2% BI allowance.
 */
export function amounts_close_for_po_match(settle_dollars: number, total_dollars: number): boolean {
  const total_cents = dollars_to_cents(total_dollars)
  if (total_cents <= 0) {
    return false
  }
  return Math.abs(dollars_to_cents(settle_dollars) - total_cents) <= Math.round(total_cents * 0.05)
}

/**
 * The one invoice a line's PO points at, when exactly one candidate carries that PO as its
 * reference and its total agrees with what the check settles. Ambiguity links nothing.
 */
export function po_matched_invoice(
  line: { po_number?: string | null; settle_dollars: number },
  candidates: AccountingInvoiceJson[]
): AccountingInvoiceJson | undefined {
  if (!po_match_key(line.po_number)) {
    return undefined
  }
  const by_reference = dedupe_invoices_by_id(candidates.filter((inv) => invoice_reference_matches_po(inv, line.po_number)))
  if (by_reference.length !== 1) {
    return undefined
  }
  const [only] = by_reference
  return amounts_close_for_po_match(line.settle_dollars, only.total_amount) ? only : undefined
}

/** Higher = better match. Deterministic for same inputs. */
export function score_accounting_invoice_match(
  context: InvoiceRecommendationContext,
  inv: Pick<AccountingInvoiceJson, "invoice_number" | "total_amount" | "invoice_date" | "id" | "reference">
): number {
  const left = normalize_invoice_number_for_match(context.invoice_number)
  const right = normalize_invoice_number_for_match(inv.invoice_number)
  const leftAlnum = invoice_number_alphanumeric_only(context.invoice_number)
  const rightAlnum = invoice_number_alphanumeric_only(inv.invoice_number)

  let stringScore = 0
  // A PO placeholder (`SI3876078` for PO `03876078`) names no invoice; resemblance to it is noise.
  const number_is_placeholder = invoice_number_is_po_placeholder(context.invoice_number, context.po_number)
  if (left.length > 0 && right.length > 0 && !number_is_placeholder) {
    if (left === right) {
      stringScore = 4_000_000
    } else if (leftAlnum.length > 0 && leftAlnum === rightAlnum) {
      stringScore = 3_200_000
    } else if (invoice_prefix_only_match(context.invoice_number, inv.invoice_number)) {
      // Same number under a doc-type prefix (e.g. "US-001234" vs "SI US-001234") — formatting-only.
      stringScore = 3_200_000
    } else if (invoice_bare_number_match(context.invoice_number, inv.invoice_number)) {
      // Same number under a glued prefix / UNFI line suffix ("33259" vs "INV33259") — formatting-only.
      stringScore = 3_200_000
    } else if (left.includes(right) || right.includes(left)) {
      stringScore = 1_600_000
    } else if (leftAlnum.length >= 4 && rightAlnum.length >= 4 && (leftAlnum.includes(rightAlnum) || rightAlnum.includes(leftAlnum))) {
      stringScore = 800_000
    }
  }
  if (stringScore < 3_200_000 && invoice_reference_matches_po(inv, context.po_number)) {
    // The invoice carries this line's PO as its reference — as good as a formatting-only number match.
    stringScore = 3_200_000
  }

  const wantCents = dollars_to_cents(context.amount_dollars)
  const haveCents = dollars_to_cents(inv.total_amount)
  const diff = Math.abs(wantCents - haveCents)
  let amountScore = 0
  if (diff === 0) {
    amountScore = 2_000_000
  } else if (diff <= 1) {
    amountScore = 1_500_000
  } else if (diff <= 100) {
    amountScore = 1_200_000
  } else {
    const penalty = Math.min(diff / 50, 1_100_000)
    amountScore = Math.max(0, 1_000_000 - penalty)
  }

  const parsed = invoice_date_ms(inv.invoice_date)
  const dateBoost = parsed === 0 ? 0 : Math.min(parsed / 1e12, 0.999)

  const idTie = parseInt(inv.id.replace(/\D/g, "").slice(-8), 10) || 0
  const idFrac = (idTie % 1000) / 1_000_000

  return stringScore + amountScore + dateBoost + idFrac
}

export function compare_accounting_invoices_by_recommendation(
  a: AccountingInvoiceJson,
  b: AccountingInvoiceJson,
  context: InvoiceRecommendationContext
): number {
  const sa = score_accounting_invoice_match(context, a)
  const sb = score_accounting_invoice_match(context, b)
  if (sb !== sa) {
    return sb - sa
  }
  const da = invoice_date_ms(a.invoice_date)
  const db = invoice_date_ms(b.invoice_date)
  if (db !== da) {
    return db - da
  }
  const na = a.invoice_number.toLowerCase()
  const nb = b.invoice_number.toLowerCase()
  if (na < nb) {
    return -1
  }
  if (na > nb) {
    return 1
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

export function sort_invoices_by_recommendation(
  invoices: AccountingInvoiceJson[],
  context: InvoiceRecommendationContext
): AccountingInvoiceJson[] {
  // Decorate-sort-undecorate. `compare_accounting_invoices_by_recommendation` re-scores both of its
  // operands on every call, and scoring runs four regex normalizations plus a date parse; a sort
  // does ~n·log n comparisons, so an org with 72k invoices paid for ~2.4M scorings per sorted list
  // and the apply page froze. Scoring once per invoice makes it n.
  const decorated = invoices.map((inv) => ({
    inv,
    score: score_accounting_invoice_match(context, inv),
    date: invoice_date_ms(inv.invoice_date),
    number: inv.invoice_number.toLowerCase(),
  }))
  decorated.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score
    }
    if (b.date !== a.date) {
      return b.date - a.date
    }
    if (a.number < b.number) {
      return -1
    }
    if (a.number > b.number) {
      return 1
    }
    return a.inv.id < b.inv.id ? -1 : a.inv.id > b.inv.id ? 1 : 0
  })
  return decorated.map((d) => d.inv)
}

/** What an open invoice picker renders: the best matches up to its cap, and how many matched in all. */
export interface PickerInvoiceList {
  visible: AccountingInvoiceJson[]
  /** Invoices matching the search, before the cap. */
  matched_count: number
}

/**
 * The rows an open `AccountingInvoicePicker` shows: invoices matching `search_query`, best match
 * first, capped at `limit`, with the current selection kept visible even when it sorts past the cap.
 *
 * Scores each invoice once (via `sort_invoices_by_recommendation`). The picker used to sort with
 * `compare_accounting_invoices_by_recommendation`, which re-scores both operands of every comparison,
 * and did so for every row on every page render with the dropdown closed. On Super Coffee's Walmart
 * checks (9,978 NetSuite invoices, up to 188 invoice lines) one render cost 1.1s for a 6-line check
 * and 35s for the 188-line one.
 */
export function picker_invoice_list(
  unique_invoices: AccountingInvoiceJson[],
  search_query: string,
  context: InvoiceRecommendationContext,
  selected_id: string | null,
  limit: number
): PickerInvoiceList {
  const matched = search_query.trim() ? unique_invoices.filter((inv) => invoice_matches_search(inv, search_query)) : unique_invoices
  const sorted = sort_invoices_by_recommendation(matched, context)
  const visible = sorted.slice(0, limit)
  if (selected_id && !visible.some((inv) => inv.id === selected_id)) {
    const selected = sorted.find((inv) => inv.id === selected_id)
    if (selected) {
      visible.unshift(selected)
    }
  }
  return { visible, matched_count: sorted.length }
}

/**
 * The single best-scoring invoice under the same ordering `sort_invoices_by_recommendation` uses,
 * found in one pass. Callers that only want the top candidate must not pay for a full sort.
 */
export function best_invoice_by_recommendation(
  invoices: AccountingInvoiceJson[],
  context: InvoiceRecommendationContext
): AccountingInvoiceJson | undefined {
  let best: { inv: AccountingInvoiceJson; score: number; date: number; number: string } | undefined
  for (const inv of invoices) {
    const candidate = {
      inv,
      score: score_accounting_invoice_match(context, inv),
      date: invoice_date_ms(inv.invoice_date),
      number: inv.invoice_number.toLowerCase(),
    }
    if (best === undefined || better_than(candidate, best)) {
      best = candidate
    }
  }
  return best?.inv
}

/** The tie-break chain of `sort_invoices_by_recommendation`, over already-scored candidates. */
function better_than(
  a: { score: number; date: number; number: string; inv: AccountingInvoiceJson },
  b: { score: number; date: number; number: string; inv: AccountingInvoiceJson }
): boolean {
  if (a.score !== b.score) {
    return a.score > b.score
  }
  if (a.date !== b.date) {
    return a.date > b.date
  }
  if (a.number !== b.number) {
    return a.number < b.number
  }
  return a.inv.id < b.inv.id
}

/**
 * Accounting invoices one line of the apply page may not pick because another line of the *same
 * check* has them: the page's own selections, plus links persisted for this check's other lines.
 *
 * A link from a line on a different check is deliberately not a reservation. UNFI pays one brand
 * invoice across several checks (Compostic's SI-00003941: SI00003941 on check 368813, SI00003941A on
 * 371747), and reserving org-wide left the second installment with no invoice to pick — "Selected on
 * another line" on a page with one line. That case is a partial payment, handled by
 * `compute_settlement_breakdown`; the picker just says which check the rest is on.
 */
export function build_reserved_accounting_invoice_ids(
  invoices: AccountingInvoiceJson[],
  local_assignments: Record<string, string>,
  current_deduction_id: string,
  check_deduction_ids: Set<string>,
  line_settle_cents?: Map<string, number>
): Set<string> {
  return plan_invoice_reservations(invoices, local_assignments, current_deduction_id, check_deduction_ids, line_settle_cents).reserved
}

/** What one line of the apply page may pick, given what the check's other lines already hold. */
export interface InvoiceReservations {
  /** Invoices the other lines of this check already settle in full: not pickable here. */
  reserved: Set<string>
  /**
   * Invoices another line of this check settles only in part, with the cents still unsettled.
   * Pickable: UNFI pays one brand invoice in two lines of one remittance (Compostic's SI-00004292,
   * $5,048.64, as SI00004292 $2,421.36 + SI00004292A $2,627.28 on check 382228).
   */
  shared_room_cents: Map<string, number>
}

/**
 * Which invoices the other lines of the *same check* hold, and whether they hold all of it.
 *
 * An invoice another line of this check has picked used to be reserved outright. That left the
 * second line of an invoice UNFI pays in two pieces on one check with nothing to pick ("Selected on
 * another line"), and the check could not be applied. The server never needed the rule: it sums
 * every line linked to an invoice (`plan_invoice_settlements`), as it already does for SGWS's
 * several clearing lines per invoice. So an invoice is reserved only once the lines holding it
 * settle its whole total; until then it stays pickable and the room left is reported.
 *
 * Without `line_settle_cents` (or for a line missing from it) nothing is known about how much a
 * line settles, and any pick reserves the invoice, as before.
 *
 * A link from a line on a different check is deliberately not a reservation. UNFI pays one brand
 * invoice across several checks too (Compostic's SI-00003941: SI00003941 on check 368813,
 * SI00003941A on 371747); that is a partial payment, handled by `compute_settlement_breakdown`.
 */
export function plan_invoice_reservations(
  invoices: AccountingInvoiceJson[],
  local_assignments: Record<string, string>,
  current_deduction_id: string,
  check_deduction_ids: Set<string>,
  line_settle_cents?: Map<string, number>
): InvoiceReservations {
  // Which invoice each *other* line of this check holds. The page's own pick wins over a stored
  // link for the same line: a line the user moved (or cleared) no longer holds its old invoice.
  const holder_invoice = new Map<string, string>()
  for (const [did, inv_id] of Object.entries(local_assignments)) {
    if (did !== current_deduction_id && inv_id) {
      holder_invoice.set(did, inv_id)
    }
  }
  const total_cents = new Map<string, number>()
  for (const inv of invoices) {
    if (!total_cents.has(inv.id)) {
      total_cents.set(inv.id, dollars_to_cents(inv.total_amount))
    }
    const did = inv.assigned_id
    if (did && did !== current_deduction_id && check_deduction_ids.has(did) && !(did in local_assignments)) {
      holder_invoice.set(did, inv.id)
    }
  }
  const covered_cents = new Map<string, number>()
  const unknown = new Set<string>()
  for (const [did, inv_id] of holder_invoice) {
    const settle = line_settle_cents?.get(did)
    if (settle === undefined) {
      unknown.add(inv_id)
      continue
    }
    covered_cents.set(inv_id, (covered_cents.get(inv_id) ?? 0) + settle)
  }
  const reserved = new Set<string>(unknown)
  const shared_room_cents = new Map<string, number>()
  for (const [inv_id, covered] of covered_cents) {
    if (unknown.has(inv_id)) {
      continue
    }
    const room = (total_cents.get(inv_id) ?? 0) - covered
    if (room > 0) {
      shared_room_cents.set(inv_id, room)
    } else {
      reserved.add(inv_id)
    }
  }
  return { reserved, shared_room_cents }
}

/** Per invoice line, the cents it settles of its invoice (cash + early-pay discount). */
export function line_settle_cents_by_id(
  lines: { deduction_id: string; deduction_amount: number; settle_amount?: number | null }[]
): Map<string, number> {
  return new Map(lines.map((l) => [l.deduction_id, dollars_to_cents(l.settle_amount ?? l.deduction_amount)]))
}

/** What lines on *other* checks settle of one accounting invoice, and which checks they sit on. */
export interface InvoiceLinkSummary {
  settled_elsewhere_cents: number
  check_numbers: string[]
}

/**
 * Per accounting invoice, the links from transactions outside this check: the invoice list carries
 * one row per link with the linked transaction's check number and settle amount. This is the "paid
 * elsewhere" side of a partial payment, and the hint the picker shows next to such an invoice.
 */
export function summarize_invoice_links(
  invoices: AccountingInvoiceJson[],
  check_deduction_ids: Set<string>
): Map<string, InvoiceLinkSummary> {
  const out = new Map<string, InvoiceLinkSummary>()
  for (const inv of invoices) {
    if (!inv.assigned_id || check_deduction_ids.has(inv.assigned_id)) {
      continue
    }
    const summary = out.get(inv.id) ?? { settled_elsewhere_cents: 0, check_numbers: [] }
    const settle = inv.assigned_settle_amount === undefined || inv.assigned_settle_amount === null ? null : Number(inv.assigned_settle_amount)
    if (settle !== null && Number.isFinite(settle)) {
      summary.settled_elsewhere_cents += dollars_to_cents(settle)
    }
    if (inv.assigned_check_number && !summary.check_numbers.includes(inv.assigned_check_number)) {
      summary.check_numbers.push(inv.assigned_check_number)
    }
    out.set(inv.id, summary)
  }
  return out
}

/** The invoice list carries one row per link; the picker shows each invoice once (first row wins). */
export function dedupe_invoices_by_id(invoices: AccountingInvoiceJson[]): AccountingInvoiceJson[] {
  const seen = new Set<string>()
  const out: AccountingInvoiceJson[] = []
  for (const inv of invoices) {
    if (seen.has(inv.id)) {
      continue
    }
    seen.add(inv.id)
    out.push(inv)
  }
  return out
}

/** The picker's hint for an invoice other checks are paying part of: "Also paid on check 368813 ($2,997.30)". */
export function describe_invoice_links(summary: InvoiceLinkSummary | undefined): string | null {
  if (!summary || summary.check_numbers.length === 0) {
    return null
  }
  const checks = `check${summary.check_numbers.length === 1 ? "" : "s"} ${summary.check_numbers.join(", ")}`
  const amount = summary.settled_elsewhere_cents > 0 ? ` (${exactCurrencyFormatter(summary.settled_elsewhere_cents / 100)})` : ""
  return `Also paid on ${checks}${amount}`
}

/** Invoices already "taken" for greedy auto-pick: initial picks + invoices linked in DB to deductions outside this check. */
export function used_accounting_invoice_ids_for_apply_page(
  check_deduction_ids: Set<string>,
  initial_assignments: Record<string, string>,
  invoices: AccountingInvoiceJson[]
): Set<string> {
  const used = new Set<string>()
  for (const id of Object.values(initial_assignments)) {
    if (id) {
      used.add(id)
    }
  }
  for (const inv of invoices) {
    if (inv.assigned_id && !check_deduction_ids.has(inv.assigned_id)) {
      used.add(inv.id)
    }
  }
  return used
}

/**
 * Whether a remittance line numbers a lettered piece of an invoice: its number, punctuation aside,
 * is the invoice's plus one trailing letter (UNFI's SI00004292A for Xero's SI-00004292).
 */
export function is_letter_suffixed_piece(line_number: string, invoice_number: string): boolean {
  const line = invoice_number_alphanumeric_only(line_number)
  const invoice = invoice_number_alphanumeric_only(invoice_number)
  return invoice.length > 0 && line.length === invoice.length + 1 && line.startsWith(invoice) && /[a-z]$/.test(line)
}

/**
 * Greedy conservative auto-assign for invoice-kind lines (unique invoices per line).
 * Mutates a copy of `initial` and returns it.
 */
export function seed_auto_assignments_for_apply_page(
  invoice_lines: {
    deduction_id: string
    invoice_number: string
    deduction_amount: number
    po_number?: string | null
    /** What the check settles of the invoice (cash + early-pay discount); defaults to `deduction_amount`. */
    settle_amount?: number | null
  }[],
  invoices: AccountingInvoiceJson[],
  initial_assignments: Record<string, string>
): Record<string, string> {
  const next = { ...initial_assignments }
  const check_deduction_ids = new Set(invoice_lines.map((l) => l.deduction_id))
  // Built once and then extended in place as picks are made. Recomputing it per line rescanned the
  // whole invoice list for a set whose invoice-derived half never changes.
  const used = used_accounting_invoice_ids_for_apply_page(check_deduction_ids, next, invoices)
  // Invoices linked to a line on another check: never shared by auto-assign (a partial payment is
  // the user's call). Everything else in `used` is held by this check's own lines, and an invoice
  // they settle only in part may take a second line of the same check that fits in what is left
  // (UNFI's SI00004292 + SI00004292A for Compostic's SI-00004292).
  const linked_elsewhere = new Set(invoices.filter((i) => i.assigned_id && !check_deduction_ids.has(i.assigned_id)).map((i) => i.id))
  const settle_cents = line_settle_cents_by_id(invoice_lines)
  const total_cents = new Map(invoices.map((i) => [i.id, dollars_to_cents(i.total_amount)]))
  const covered_cents = new Map<string, number>()
  for (const [did, inv_id] of Object.entries(next)) {
    const settle = settle_cents.get(did)
    if (inv_id && settle !== undefined) {
      covered_cents.set(inv_id, (covered_cents.get(inv_id) ?? 0) + settle)
    }
  }
  function has_room_for(inv_id: string, cents: number): boolean {
    const covered = covered_cents.get(inv_id)
    return covered !== undefined && !linked_elsewhere.has(inv_id) && covered + cents <= (total_cents.get(inv_id) ?? 0)
  }
  function take(deduction_id: string, inv_id: string) {
    next[deduction_id] = inv_id
    used.add(inv_id)
    covered_cents.set(inv_id, (covered_cents.get(inv_id) ?? 0) + (settle_cents.get(deduction_id) ?? 0))
  }

  for (const line of invoice_lines) {
    if (next[line.deduction_id]) {
      continue
    }
    const line_cents = settle_cents.get(line.deduction_id) ?? 0
    const candidates = invoices.filter((i) => !used.has(i.id) || has_room_for(i.id, line_cents))
    // A PO placeholder names no invoice, so only its PO may link it (below).
    if (!invoice_number_is_po_placeholder(line.invoice_number, line.po_number)) {
      // Only the top candidate matters here, so take the max in one pass rather than sorting the whole
      // list per line — sorting made this O(lines x invoices x log invoices) scorings.
      const best = best_invoice_by_recommendation(candidates, {
        invoice_number: line.invoice_number,
        amount_dollars: line.deduction_amount,
      })
      if (
        best &&
        should_auto_assign_accounting_invoice(
          { invoice_number: line.invoice_number, amount_dollars: line.deduction_amount },
          best
        )
      ) {
        take(line.deduction_id, best.id)
        continue
      }
    }
    // A UNFI letter-suffixed piece (SI00004292A) of an invoice another line of this check already
    // pays in part: the one invoice numbered by its stem with room left for it.
    const piece_of = invoices.filter(
      (i) => used.has(i.id) && has_room_for(i.id, line_cents) && is_letter_suffixed_piece(line.invoice_number, i.invoice_number)
    )
    if (piece_of.length === 1) {
      take(line.deduction_id, piece_of[0].id)
      continue
    }
    // Fallback: the one invoice that carries this line's PO as its reference.
    const by_po = po_matched_invoice(
      { po_number: line.po_number, settle_dollars: line.settle_amount ?? line.deduction_amount },
      candidates
    )
    if (by_po) {
      take(line.deduction_id, by_po.id)
    }
  }
  return next
}

/**
 * One-pass index of an invoice list for `describe_unassigned_invoice_line`: invoices by the digits
 * of their number and by the PO key of their reference. Built once per list, so explaining every
 * unassigned line costs a lookup each rather than a scan of the customer's whole invoice history.
 */
export interface InvoiceLookup {
  number_digit_keys: Set<string>
  by_po_key: Map<string, AccountingInvoiceJson[]>
}

function invoice_number_digit_key(raw: string): string {
  return raw.replace(/\D/g, "").replace(/^0+/, "")
}

export function build_invoice_lookup(invoices: AccountingInvoiceJson[]): InvoiceLookup {
  const number_digit_keys = new Set<string>()
  const by_po_key = new Map<string, AccountingInvoiceJson[]>()
  for (const inv of dedupe_invoices_by_id(invoices)) {
    const digits = invoice_number_digit_key(inv.invoice_number)
    if (digits) {
      number_digit_keys.add(digits)
    }
    const po = po_match_key(inv.reference)
    if (po) {
      by_po_key.set(po, [...(by_po_key.get(po) ?? []), inv])
    }
  }
  return { number_digit_keys, by_po_key }
}

/**
 * Why an invoice line on the Apply Cash page has no accounting invoice yet, in words the user can
 * act on — or `null` when the list holds a plausible candidate for the picker. Before this, a line
 * whose remitted number named nothing (KeHE's `SI3876078`) looked like any other line waiting for a
 * pick, and the brand only learned the invoice did not exist when posting went wrong.
 */
export function describe_unassigned_invoice_line(
  line: { invoice_number: string; po_number?: string | null },
  lookup: InvoiceLookup,
  system_label: string
): string | null {
  const po = line.po_number?.trim() || null
  const po_key = po_match_key(po)
  const by_reference = po_key ? (lookup.by_po_key.get(po_key) ?? []) : []
  if (invoice_number_is_po_placeholder(line.invoice_number, po)) {
    const listed = `The remittance lists PO ${po} as the invoice number, so ${line.invoice_number} does not exist in ${system_label}.`
    if (by_reference.length > 0) {
      const numbers = by_reference.map((inv) => inv.invoice_number).join(" or ")
      return `${listed} Pick the invoice that references that PO (${numbers}).`
    }
    return `${listed} No synced ${system_label} invoice references PO ${po_key} — find the invoice for that PO in ${system_label} before applying.`
  }
  const digits = invoice_number_digit_key(line.invoice_number)
  if (by_reference.length > 0 || (digits && lookup.number_digit_keys.has(digits))) {
    return null
  }
  const po_clause = po_key ? ` or references PO ${po}` : ""
  return `No synced ${system_label} invoice is numbered ${line.invoice_number}${po_clause}. Confirm it exists in ${system_label} and has synced before applying.`
}

/**
 * The apply-blocking message for unassigned invoice lines: the generic instruction plus, for each
 * line with no plausible candidate, why (see `describe_unassigned_invoice_line`).
 */
export function build_unassigned_invoices_error(line_hints: { invoice_number: string; hint: string | null }[]): string {
  const hints = line_hints.filter((l) => l.hint).map((l) => `${l.invoice_number}: ${l.hint}`)
  return hints.length === 0 ? "Assign every invoice before applying." : `Assign every invoice before applying. ${hints.join(" ")}`
}

/**
 * Conservative auto-assign: (1) exact normalized invoice #, (2) exact alphanumeric core (formatting-only
 * difference), (3) fuzzy-close invoice core **and** amount within min($1, 1%), or (4) legacy: exact cents
 * amount + substring-level string score from `score_accounting_invoice_match`.
 */
export function should_auto_assign_accounting_invoice(
  context: InvoiceRecommendationContext,
  inv: AccountingInvoiceJson
): boolean {
  const left = normalize_invoice_number_for_match(context.invoice_number)
  const right = normalize_invoice_number_for_match(inv.invoice_number)
  const leftAlnum = invoice_number_alphanumeric_only(context.invoice_number)
  const rightAlnum = invoice_number_alphanumeric_only(inv.invoice_number)
  const wantCents = dollars_to_cents(context.amount_dollars)
  const haveCents = dollars_to_cents(inv.total_amount)
  const amountExact = wantCents === haveCents
  if (left.length > 0 && right.length > 0 && left === right) {
    return true
  }
  if (leftAlnum.length > 0 && leftAlnum === rightAlnum) {
    return true
  }
  // Same invoice number under a doc-type prefix (e.g. "US-001234" vs Xero's "SI US-001234"). This is a
  // formatting difference, so auto-assign even when the amount differs (early-pay discounts make the
  // invoice total and deduction amount legitimately differ).
  if (invoice_prefix_only_match(context.invoice_number, inv.invoice_number)) {
    return true
  }
  // Same again for a prefix glued onto the digits ("33259" vs NetSuite's "INV33259") or a UNFI line
  // suffix ("INV33141-0-East" vs "INV33141"). Before this rule, a discounted Walmart invoice only
  // matched when its cash equalled the NetSuite total to the cent — i.e. never.
  if (invoice_bare_number_match(context.invoice_number, inv.invoice_number)) {
    return true
  }
  if (
    invoice_numbers_fuzzy_close(context.invoice_number, inv.invoice_number) &&
    amounts_close_for_auto_assign(context.amount_dollars, inv.total_amount)
  ) {
    return true
  }
  const s = score_accounting_invoice_match(context, inv)
  return amountExact && s >= 1_600_000
}

/** Minimum score to show the "Recommended" chip on a row. */
export const RECOMMENDED_INVOICE_BADGE_MIN_SCORE = 3_200_000

export function invoice_matches_search(inv: AccountingInvoiceJson, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) {
    return true
  }
  const parts = [
    inv.invoice_number,
    inv.invoice_date ?? "",
    String(inv.total_amount),
    exact_currency_search_fragment(inv.total_amount),
  ]
  return parts.some((p) => p.toLowerCase().includes(q))
}

/**
 * One shared formatter: `toLocaleString` with options builds a new `Intl.NumberFormat` per call, and
 * the picker search formats every invoice on each keystroke (150ms per key over Walmart's 9,978).
 */
const usd_search_formatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

function exact_currency_search_fragment(amount: number): string {
  try {
    return usd_search_formatter.format(amount)
  } catch {
    return ""
  }
}

/**
 * Signed amount a coded split contributes to the credit memo. Mirrors the server: the contribution
 * negates the transaction's economic direction, so a normal deduction (negative amount — money taken)
 * is a positive credit, while a positive-amount transaction (a reversal/tax credit or a repayment,
 * where the customer pays money back) reduces the credit. Magnitude is the split's absolute value.
 */
export function credit_memo_split_amount(invoice_amount: number, split_amount: number): number {
  const magnitude = Math.abs(split_amount)
  return invoice_amount > 0 ? -magnitude : magnitude
}

export interface PaymentBalanceInput {
  /** Total of the accounting invoices selected to be paid. */
  selected_invoice_total_cents: number
  /** Cash on the check. */
  check_cash_cents: number
  /** Net credit memo total (deductions minus repayments). */
  credit_memo_total_cents: number
  /** Early-pay discounts taken on the payment ("Disc. Taken"); 0 unless they ride on the payment. */
  early_pay_discount_cents?: number
  /** Cash of the invoice lines left unapplied on the customer's account; 0 for almost every check. */
  unapplied_cash_cents?: number
  /** check_cash_cents - unapplied_cash_cents + credit_memo_total_cents + early_pay_discount_cents. */
  applied_total_cents: number
  /** selected_invoice_total_cents - applied_total_cents. */
  delta_cents: number
  /**
   * What the check's own remittance lines add up to (invoice payments minus deductions plus
   * repayments). When it disagrees with `check_cash_cents` the check does not foot — the usual
   * cause of an imbalance on a check whose amount was typed in by hand — and the message says so.
   */
  remittance_lines_net_cents?: number
}

/**
 * When a check's amount disagrees with its own remittance lines, the sentence that says so — the
 * imbalance is then a wrong check amount or a missing/extra line, not the invoice selection or the
 * coding. Empty when the check foots (or the lines' net is unknown).
 */
export function build_check_footing_note(check_cash_cents: number, remittance_lines_net_cents: number | undefined): string {
  if (remittance_lines_net_cents === undefined || remittance_lines_net_cents === check_cash_cents) {
    return ""
  }
  const gap_cents = check_cash_cents - remittance_lines_net_cents
  const direction = gap_cents > 0 ? "more" : "less"
  return (
    `This check does not foot: its amount (${exactCurrencyFormatter(check_cash_cents / 100)}) is ` +
    `${exactCurrencyFormatter(Math.abs(gap_cents) / 100)} ${direction} than its remittance lines add up to ` +
    `(${exactCurrencyFormatter(remittance_lines_net_cents / 100)}). Check the amount entered for the check against the ` +
    `deposit, and the remittance for a line that is missing from or does not belong on this check.`
  )
}

/**
 * A plain-language explanation of why the apply-cash sides don't balance. The two sides are: the
 * invoices being paid, and the cash + credit memo paying them. Spells out both totals, their sum, and
 * the gap (with direction) so the user knows which way to adjust. The credit memo can be negative when
 * repayments outweigh deductions, so the wording avoids assuming a sign.
 */
export function build_payment_balance_error(input: PaymentBalanceInput): string {
  const invoices = exactCurrencyFormatter(input.selected_invoice_total_cents / 100)
  const cash = exactCurrencyFormatter(input.check_cash_cents / 100)
  const credit = exactCurrencyFormatter(input.credit_memo_total_cents / 100)
  const applied = exactCurrencyFormatter(input.applied_total_cents / 100)
  const gap = exactCurrencyFormatter(Math.abs(input.delta_cents) / 100)
  const discount_cents = input.early_pay_discount_cents ?? 0
  const unapplied_cents = input.unapplied_cash_cents ?? 0

  // Naming the discounts (and the cash left unapplied) only when there are some keeps the common
  // message unchanged.
  const cash_clause = unapplied_cents
    ? `check cash (${cash}) less the ${exactCurrencyFormatter(unapplied_cents / 100)} left unapplied`
    : `check cash (${cash})`
  const funding = discount_cents
    ? `${cash_clause} plus the credit memo (${credit}) plus early-pay discounts taken on the payment (${exactCurrencyFormatter(
        discount_cents / 100
      )})`
    : `${cash_clause} plus the credit memo (${credit})`
  const covered = discount_cents ? "the cash, credit memo, and discounts" : "the cash and credit memo"

  const direction =
    input.delta_cents > 0
      ? `the selected invoices are ${gap} more than ${covered} cover`
      : `${covered} are ${gap} more than the selected invoices`

  const footing_note = build_check_footing_note(input.check_cash_cents, input.remittance_lines_net_cents)
  return (
    `These two sides need to match before you can apply cash. ` +
    `Selected invoices total ${invoices}, while ${funding} comes to ${applied} — ${direction}. ` +
    (footing_note ? footing_note : `Adjust the selected invoices or the deduction coding so the two sides are equal.`)
  )
}

/** One transaction coded for more than the check carries for it, from the preview's `overcoded_transactions`. */
export interface OvercodedTransactionRow {
  invoice_number: string
  kind: string
  codeable_amount: number | string
  coded_amount: number | string
  excess_amount: number | string
}

/**
 * A plain-language explanation of transactions coded for more than the check carries for them —
 * the mirror of `build_uncoded_transactions_error`, and like it reported in place of the balance
 * gap. KeHE's SI00003705A repayment on Compostic's check 1382732 arrived at $2,414.16 (gross
 * $2,463.43 less the terms discount KeHE kept) and was coded at the gross; the balance message
 * blamed a $49.27 invoice-selection gap on a check with no invoice to select.
 */
export function build_overcoded_transactions_error(rows: OvercodedTransactionRow[]): string {
  if (rows.length === 0) {
    return ""
  }
  const kind_label = (kind: string) => (kind === "repayment" ? "repayment" : kind === "invoice" ? "early-pay discount" : "deduction")
  const detail = rows
    .map(
      (r) =>
        `${r.invoice_number} is coded ${exactCurrencyFormatter(Number(r.coded_amount))} against a ${exactCurrencyFormatter(
          Number(r.codeable_amount)
        )} ${kind_label(r.kind)} (${exactCurrencyFormatter(Number(r.excess_amount))} over)`
    )
    .join("; ")
  return (
    `${rows.length === 1 ? "One transaction is" : `${rows.length} transactions are`} coded for more than this check carries: ${detail}. ` +
    `Re-code at the amount that actually arrived — a discount the customer kept is not spend — then apply.`
  )
}

/** One transaction on the check that carries no GL coding, from the preview's `uncoded_transactions`. */
export interface UncodedTransactionRow {
  invoice_number: string
  kind: string
  missing_amount: number
}

/**
 * A plain-language explanation of transactions whose dollars are missing from the credit memo
 * because they have no GL coding.
 *
 * This is the *cause* of an unbalanced apply, so it replaces the balance message rather than
 * joining it: an uncoded transaction's cash still counts toward the check total while contributing
 * nothing to the credit memo, so the two sides can never match until it is coded. Reporting only
 * the gap sent users to re-check their invoice selection, which was never the problem.
 *
 * Returns `""` when nothing is uncoded, so the caller can treat it as "no error".
 */
export function build_uncoded_transactions_error(rows: UncodedTransactionRow[]): string {
  if (rows.length === 0) {
    return ""
  }
  const total_cents = rows.reduce((sum: number, row: UncodedTransactionRow) => sum + dollars_to_cents(row.missing_amount), 0)
  const listed = rows
    .map((row: UncodedTransactionRow) => `${row.invoice_number} (${row.kind}, ${exactCurrencyFormatter(row.missing_amount)})`)
    .join(", ")
  const subject = rows.length === 1 ? "This transaction has" : `These ${rows.length} transactions have`
  return (
    `${subject} no GL coding, so ${exactCurrencyFormatter(total_cents / 100)} of the check is missing from the credit memo ` +
    `and the two sides can't balance: ${listed}. Code them, then come back to apply cash — ` +
    `their cash is already counted in the check total, so the credit memo has to account for them too.`
  )
}

/** A credit memo line as the product group and chargeback type notes read it. */
export interface ProductGroupLineRow {
  gl_account_id: string
  amount: number | string
  product_group?: string | null
  chargeback_type?: string | null
}

/** "2 lines ($25.35, GL 48003, 48005) have" — the lines a field-missing note is about. */
function describe_lines_missing_field(missing: ProductGroupLineRow[]): string {
  const total_cents = missing.reduce((sum: number, line: ProductGroupLineRow) => sum + dollars_to_cents(Number(line.amount)), 0)
  const accounts = [...new Set(missing.map((line: ProductGroupLineRow) => line.gl_account_id))].join(", ")
  const subject = missing.length === 1 ? "1 line" : `${missing.length} lines`
  return `${subject} (${exactCurrencyFormatter(total_cents / 100)}, GL ${accounts}) ha${missing.length === 1 ? "s" : "ve"}`
}

/**
 * The warning shown when the org's credit memo lines carry a chargeback type (1st Phorm's NetSuite
 * "Chargeback Type") and some lines have none: those post with the field blank. Never blocks the
 * apply. Empty when the org has no such field or every line has a type.
 */
export function build_missing_chargeback_type_note(lines: ProductGroupLineRow[], field_name: string | null | undefined): string {
  if (!field_name) {
    return ""
  }
  const missing = lines.filter((line: ProductGroupLineRow) => !line.chargeback_type?.trim())
  if (missing.length === 0) {
    return ""
  }
  return (
    `${describe_lines_missing_field(missing)} no ${field_name}. They will post with ${field_name} blank — ` +
    `to fill it, open the transactions under the line and pick a ${field_name} on their coding before applying.`
  )
}

/**
 * The warning shown when the org's credit memo lines carry a product group (1st Phorm's NetSuite
 * "Related Product Group") and some lines have none: those post with the field blank. Never blocks
 * the apply. Empty when the org has no such field or every line has a group.
 */
export function build_missing_product_group_note(lines: ProductGroupLineRow[], field_name: string | null | undefined): string {
  if (!field_name) {
    return ""
  }
  const missing = lines.filter((line: ProductGroupLineRow) => !line.product_group?.trim())
  if (missing.length === 0) {
    return ""
  }
  return (
    `${describe_lines_missing_field(missing)} no ${field_name}: ` +
    `the backup didn't name a product we could match. They will post with ${field_name} blank — ` +
    `to fill it, open the transactions under the line and pick a ${field_name} on their coding before applying.`
  )
}

/** The preview's `repayment_invoice`, as the note builders need it. */
export interface RepaymentInvoiceRow {
  reference: string
  total: number | string
  credit_total: number | string
  supported: boolean
  accounting_system: string
  customer_name?: string | null
  lines: { gl_account_id: string; gl_account_name?: string | null; amount: number | string }[]
}

/**
 * Plain-language account of what applying a repayment check does, or `null` when the check is not
 * one. The check's credit memo nets below zero — the customer is paying money back, and the
 * deductions on the check are smaller than the repayment — so nothing on the check can receive the
 * cash. Where the accounting system supports it, the apply creates a sales invoice for the
 * repayment itself and the check's cash and deductions settle it, so this is the lead-in to
 * [`build_repayment_invoice_steps`]; elsewhere it says to book the check by hand.
 */
export function build_repayment_invoice_note(row: RepaymentInvoiceRow | null | undefined): string | null {
  if (!row) return null
  const total = Number(row.total)
  const credits = Number(row.credit_total)
  const who = row.customer_name?.trim() || "the customer"
  const deducted = Math.round(credits * 100) > 0 ? ` (more than the ${exactCurrencyFormatter(credits)} deducted on it)` : ""
  const situation = `This check includes ${exactCurrencyFormatter(total)} that ${who} is paying back${deducted}.`
  if (!row.supported) {
    return (
      `${situation} A payment has to be applied to an invoice, and there is no invoice for this money. MarginWiz can create one ` +
      `only in Xero, QuickBooks or NetSuite, so record this check in ${row.accounting_system} by hand.`
    )
  }
  return `${situation} A payment in ${row.accounting_system} has to be applied to an invoice, so when you click Apply, MarginWiz will:`
}

/**
 * What the apply does for a repayment check, step by step, for the user to review before applying.
 * Empty when the check is not one, or when the accounting system has no way to post it.
 */
export function build_repayment_invoice_steps(row: RepaymentInvoiceRow | null | undefined): string[] {
  if (!row || !row.supported) return []
  const total = Number(row.total)
  const credits = Number(row.credit_total)
  const cash_cents = Math.round(total * 100) - Math.round(credits * 100)
  const who = row.customer_name?.trim() || "the customer"
  const accounts = row.lines
    .map((line) => `${line.gl_account_id}${line.gl_account_name ? ` ${line.gl_account_name}` : ""}`)
    .filter((account: string, index: number, all: string[]) => all.indexOf(account) === index)
    .join(", ")
  const steps = [`Create a ${row.accounting_system} invoice to ${who} for ${exactCurrencyFormatter(total)}, posted to ${accounts}.`]
  if (Math.round(credits * 100) > 0) {
    steps.push(`Apply the ${exactCurrencyFormatter(credits)} of deductions on this check against that invoice.`)
  }
  steps.push(`Apply the remaining ${exactCurrencyFormatter(cash_cents / 100)} of the check to it, along with the other invoices this check pays.`)
  return steps
}

/** One UNFI `PP` line from the preview's `prepayments`, already classified by the server. */
export interface PrepaymentRow {
  /** The `PP` row's own deduction id — the advance keeps it when rewritten into an invoice line. */
  deduction_id?: string
  invoice_number: string
  invoice_stem: string
  role: "self_cancelling" | "advance" | "reversal" | "partial_reversal"
  /** Decimal-serialized by the server, so it may arrive as a string. */
  amount: number | string
  settled_invoice_id?: string
  /** That line's number as printed on the remittance (`INV33141-0-East`), when known. */
  settled_invoice_number?: string
  /** `partial_reversal` only: the advance less the re-listed invoice line's cash. */
  clawback?: number | string
}

function prepayment_amount(row: PrepaymentRow): string {
  return exactCurrencyFormatter(Number(row.amount))
}

/**
 * Blocking error for reversals with no matching invoice line on this check. Their cash is inside
 * the check total with nothing to absorb it, so the server refuses to apply; surface the same
 * refusal at preview time instead of letting the user discover it on the Apply button.
 *
 * Returns `""` when every reversal is resolved.
 */
export function build_unresolved_prepayments_error(rows: PrepaymentRow[]): string {
  const unresolved = rows.filter((row: PrepaymentRow) => row.role === "reversal" && !row.settled_invoice_id)
  if (unresolved.length === 0) {
    return ""
  }
  const listed = unresolved.map((row: PrepaymentRow) => `${row.invoice_number} (${prepayment_amount(row)})`).join(", ")
  const subject = unresolved.length === 1 ? "a prepayment UNFI is taking back, but" : "prepayments UNFI is taking back, but"
  return (
    `This check carries ${subject} the invoice ${unresolved.length === 1 ? "it" : "each one"} settled is either missing ` +
    `from the check or listed for a different amount: ${listed}. ` +
    `Check the remittance before applying — the cash doesn't add up until this is explained.`
  )
}

/** One `PP` line as the apply page presents it: what UNFI did, and what applying does with it. */
export interface PrepaymentSummaryRow {
  key: string
  /** The `PP` number as printed on the remittance. */
  invoice_number: string
  invoice_stem: string
  /** Two or three words for the badge. */
  label: string
  /** `blocked` rows stop the apply; the card paints them red and repeats the blocking message. */
  tone: "info" | "blocked"
  /** Cash the line carries on the remittance, signed; `null` for a self-cancelling pair (±). */
  amount: number | null
  /** The invoice line on this check the reversal cancels, when known. */
  paired_line?: { invoice_number: string; amount: number }
  /** What cash application does with the line, in plain language. */
  note: string
}

/**
 * The resolved `PP` lines on a check, one row per line (one per *pair* for self-cancelling), in
 * the order the server classified them. Explains rows the user can see on the remittance but not
 * (or not as-is) in the tables below:
 *
 * - an **advance** appears below as a payment of the invoice it settles, numbered by the stem
 *   rather than the `PP` number on the remittance;
 * - a resolved **reversal** and the invoice line it cancels are both excluded — that invoice was
 *   already paid by the advance on an earlier check, terms discount included;
 * - a **self-cancelling** pair (advance and reversal on this one check) nets to nothing and is
 *   excluded, one row per pair;
 * - a **partial reversal** (the invoice re-listed for less than the advance) is excluded with its
 *   line, and the difference is recorded as a deduction row against the invoice below;
 * - an **unresolved** reversal (no invoice line on the check pays what the advance paid) blocks
 *   the apply and carries the same message as `build_unresolved_prepayments_error`.
 */
export function build_prepayment_summary(rows: PrepaymentRow[]): PrepaymentSummaryRow[] {
  const summary: PrepaymentSummaryRow[] = []
  const seen_pairs = new Set<string>()
  for (const row of rows) {
    const amount = Number(row.amount)
    const base = { key: `${row.invoice_number}:${row.role}`, invoice_number: row.invoice_number, invoice_stem: row.invoice_stem }
    if (row.role === "advance") {
      summary.push({
        ...base,
        label: "Paid early",
        tone: "info",
        amount,
        note:
          `${row.invoice_number}: UNFI paid invoice ${row.invoice_stem} early (${prepayment_amount(row)}, net of the ` +
          `terms discount). It appears below as that invoice's payment — assign the matching ${row.invoice_stem} invoice. ` +
          `Nothing to code.`,
      })
    } else if (row.role === "reversal" && row.settled_invoice_id) {
      const line_number = row.settled_invoice_number ?? row.invoice_stem
      summary.push({
        ...base,
        label: "Nets to zero",
        tone: "info",
        amount: -amount,
        paired_line: { invoice_number: line_number, amount },
        note:
          `${row.invoice_number}: UNFI is taking back the advance it paid for invoice ${row.invoice_stem} on an earlier ` +
          `check, and lists the invoice again here as ${line_number} (${prepayment_amount(row)}). The two cancel out and ` +
          `are left out of this application — ${row.invoice_stem} was paid, terms discount included, when the advance ` +
          `was applied. Nothing to code.`,
      })
    } else if (row.role === "reversal") {
      summary.push({
        ...base,
        label: "Needs a look",
        tone: "blocked",
        amount: -amount,
        note: build_unresolved_prepayments_error([row]),
      })
    } else if (row.role === "partial_reversal" && !row.settled_invoice_id) {
      // No invoice line at all: UNFI took the whole advance back and did not list the invoice again
      // (Compostic's SI00004145PP on 360143). The clawback is the whole advance.
      summary.push({
        ...base,
        label: "Taken back in full",
        tone: "info",
        amount: -amount,
        note:
          `${row.invoice_number}: UNFI paid invoice ${row.invoice_stem} early (${prepayment_amount(row)}) on an earlier check ` +
          `and is taking all of it back here without listing the invoice again. The reversal is excluded; the ` +
          `${exactCurrencyFormatter(Number(row.clawback ?? 0))} UNFI took back is recorded as a deduction against ` +
          `${row.invoice_stem} below — code it (or dispute it) before applying. Nothing else to do with the PP line.`,
      })
    } else if (row.role === "partial_reversal") {
      summary.push({
        ...base,
        label: "Partly taken back",
        tone: "info",
        amount: -amount,
        paired_line: row.settled_invoice_number ? { invoice_number: row.settled_invoice_number, amount: amount - Number(row.clawback ?? 0) } : undefined,
        note:
          `${row.invoice_number}: UNFI paid invoice ${row.invoice_stem} early (${prepayment_amount(row)}) on an earlier check ` +
          `and is taking that back here, but re-lists the invoice for less. The reversal and the ${row.invoice_stem} ` +
          `invoice line are excluded; the ${exactCurrencyFormatter(Number(row.clawback ?? 0))} UNFI kept is recorded as a ` +
          `deduction against ${row.invoice_stem} below — code it (or dispute it) before applying.`,
      })
    } else if (row.role === "self_cancelling" && !seen_pairs.has(row.invoice_number)) {
      seen_pairs.add(row.invoice_number)
      summary.push({
        ...base,
        label: "Nets to zero",
        tone: "info",
        amount: null,
        note:
          `${row.invoice_number}: the advance and its reversal both appear on this check and cancel out (${prepayment_amount(
            row
          )} each way); neither affects the application. Nothing to code.`,
      })
    }
  }
  return summary
}

/**
 * The informational notes from `build_prepayment_summary` — everything but the blocking rows, which
 * belong to `build_unresolved_prepayments_error`.
 */
export function build_prepayment_notes(rows: PrepaymentRow[]): string[] {
  return build_prepayment_summary(rows)
    .filter((row: PrepaymentSummaryRow) => row.tone === "info")
    .map((row: PrepaymentSummaryRow) => row.note)
}

/**
 * The `PP` remittance number behind an invoice-assignment line, when that line is an advance the
 * resolver rewrote into its invoice's payment — so the table can say "paid early as INV33141PP"
 * on a row whose invoice number no longer matches the remittance. `null` for an ordinary line.
 */
export function advance_behind_line(rows: PrepaymentRow[], deduction_id: string): PrepaymentRow | null {
  return rows.find((row: PrepaymentRow) => row.role === "advance" && row.deduction_id === deduction_id) ?? null
}

/** The preview's `rebill_settlements`, as the line note needs it. */
export interface RebillSettlementRow {
  deduction_id: string
  repayment_number: string
  repaid_invoice_number: string
  repaid_check_number?: string | null
  rebill_invoice_number: string
  amount: number | string
}

/** The rebill settlement behind an invoice line, or `null` when the line is an ordinary invoice payment. */
export function rebill_settlement_behind_line(rows: RebillSettlementRow[], deduction_id: string): RebillSettlementRow | null {
  return rows.find((row: RebillSettlementRow) => row.deduction_id === deduction_id) ?? null
}

/**
 * Why an invoice line carries a number the remittance does not: the customer is paying back a
 * deduction that was rebilled, and this payment settles the rebill invoice.
 */
export function build_rebill_settlement_note(row: RebillSettlementRow): string {
  const taken = row.repaid_check_number ? `${row.repaid_invoice_number} (taken on check ${row.repaid_check_number})` : row.repaid_invoice_number
  return `Remitted as ${row.repayment_number}: the repayment of ${taken}, which was rebilled as ${row.rebill_invoice_number}. This payment settles that invoice.`
}

/**
 * Deep link to an applied payment / credit memo in the accounting system, or `null` when the
 * provider has no usable public URL (the caller shows a static "applied" indicator instead).
 *
 * QuickBooks: stable `qbo.intuit.com/app/{kind}?txnId=` URLs. Xero: legacy `go.xero.com` view
 * URLs — they open in the user's currently-selected Xero organisation (we don't store the org
 * shortcode), which is right for single-org brands. A Xero apply stores the payment id as a
 * comma-joined list (one payment per invoice's cash portion) — link the first — or as
 * `creditnote:{id}` when the invoices were fully settled by credit notes and no cash was posted.
 * Sage Intacct / NetSuite: session-scoped URLs only → null.
 */
export function applied_remote_link(
  integration: string | null | undefined,
  kind: "payment" | "credit_memo",
  remote_id: string
): string | null {
  const provider = (integration ?? "").toLowerCase()
  const id = remote_id.trim()
  if (!id) {
    return null
  }
  if (provider === "quickbooks") {
    const qb_kind = kind === "payment" ? "recvpayment" : "creditmemo"
    return `https://qbo.intuit.com/app/${qb_kind}?txnId=${encodeURIComponent(id)}`
  }
  if (provider === "xero") {
    const credit_note_id = id.startsWith("creditnote:") ? id.slice("creditnote:".length) : null
    if (kind === "credit_memo" || credit_note_id !== null) {
      const note_id = (credit_note_id ?? id).trim()
      return note_id ? `https://go.xero.com/AccountsReceivable/ViewCreditNote.aspx?creditNoteID=${encodeURIComponent(note_id)}` : null
    }
    const first_payment = id.split(",")[0].trim()
    return first_payment ? `https://go.xero.com/Bank/ViewTransaction.aspx?bankTransactionID=${encodeURIComponent(first_payment)}` : null
  }
  return null
}

/** One selected invoice this apply would settle for other than its total, and where the rest is. */
export interface SettlementBreakdownRow {
  deduction_id: string
  invoice_number: string
  /** What this check settles of the invoice (the line's paid amount plus early-pay discount). */
  settled_cents: number
  /** Of the remainder, what lines on other checks settle — a partial payment, nothing to flag. */
  partial_cents: number
  /** The checks those lines sit on. */
  partial_check_numbers: string[]
  /** What is left after those: money nobody has remitted, a short pay. Net of `over_cents` below. */
  short_cents: number
  /** The opposite gap: settled *beyond* the invoice total, spent against the shorts. See `absorb_over_settlements`. */
  over_cents: number
  /**
   * What the accounting system already applied to the invoice as a credit note the check also lists
   * (`already_credited` on the preview). Off what this check applies and out of the memo; independent
   * of the gap above, since the invoice line still settles the full total. Mirrors the server's
   * `credited_cents`.
   */
  credited_cents: number
}

/**
 * Per-invoice attribution of the balance gap for the apply flow. Mirrors the server
 * (`plan_invoice_settlements`): each invoice-kind line's gap is its assigned accounting invoice's
 * total minus the line's `settle_amount`; the gap is partial up to what other checks' linked lines
 * settle of the same invoice (`link_summaries`), and short beyond that. Returns the rows only when
 * the whole balance gap is exactly explained — every line assigned and carrying a settle amount,
 * every gap ≥ 0, and the gaps summing to `delta_cents` — otherwise `null` (the gap is a coding
 * problem, keep the hard error). A breakdown with no short cents needs no opt-in to apply; one with
 * short cents is the apply-anyway flow. Server-side recomputation enforces the same rule on apply.
 */
export function compute_settlement_breakdown(
  invoice_lines: { deduction_id: string; settle_amount?: number | null }[],
  assignments: Record<string, string>,
  accounting_invoice_by_id: Map<string, { invoice_number: string; total_amount: number }>,
  link_summaries: Map<string, InvoiceLinkSummary>,
  delta_cents: number,
  credited_by_invoice: Map<string, number> = new Map()
): SettlementBreakdownRow[] | null {
  if (delta_cents <= 0) {
    return null
  }
  // One row per invoice, its lines' settle amounts summed — as the server does. UNFI pays one
  // invoice in two lines of one check (Compostic's SI-00004292: SI00004292 + SI00004292A), and a
  // row per line read each as short by the other's piece.
  const by_invoice = new Map<string, { deduction_id: string; settled_cents: number }>()
  for (const line of invoice_lines) {
    const invoice_id = assignments[line.deduction_id] ?? ""
    if (!accounting_invoice_by_id.has(invoice_id) || line.settle_amount === undefined || line.settle_amount === null) {
      return null
    }
    const held = by_invoice.get(invoice_id)
    const settled = dollars_to_cents(line.settle_amount) + (held?.settled_cents ?? 0)
    // The server hangs the row off the lowest transaction id; same here.
    const deduction_id = held && held.deduction_id < line.deduction_id ? held.deduction_id : line.deduction_id
    by_invoice.set(invoice_id, { deduction_id, settled_cents: settled })
  }
  const rows: SettlementBreakdownRow[] = []
  let total_cents = 0
  for (const [invoice_id, { deduction_id, settled_cents }] of by_invoice) {
    const invoice = accounting_invoice_by_id.get(invoice_id)!
    const gap_cents = dollars_to_cents(invoice.total_amount) - settled_cents
    // Already applied in the accounting system: a row even when the line settles the invoice in
    // full, because the apply still stops short of the total by this much (server: `credited_cents`).
    const credited_cents = Math.max(credited_by_invoice.get(invoice_id) ?? 0, 0)
    if (gap_cents === 0 && credited_cents === 0) {
      continue
    }
    // Settled beyond its total: recorded, then spent against the shorts below, exactly as the
    // server does. Returning null here instead let one customer's rounding — a Walmart reason 100
    // "debit balance write off" of $1.18 — collapse the breakdown for a whole 29-invoice check.
    const summary = link_summaries.get(invoice_id)
    const partial_cents = gap_cents < 0 ? 0 : Math.min(gap_cents, Math.max(summary?.settled_elsewhere_cents ?? 0, 0))
    rows.push({
      deduction_id,
      invoice_number: invoice.invoice_number,
      settled_cents,
      partial_cents,
      partial_check_numbers: partial_cents > 0 ? [...(summary?.check_numbers ?? [])] : [],
      short_cents: gap_cents < 0 ? 0 : gap_cents - partial_cents,
      over_cents: gap_cents < 0 ? -gap_cents : 0,
      credited_cents,
    })
    total_cents += gap_cents + credited_cents
  }
  absorb_over_settlements(rows)
  if (rows.length === 0 || total_cents !== delta_cents) {
    return null
  }
  return rows
}

/**
 * Spend every over-settlement's excess against the shorts on the same check, in proportion to each
 * short. Mirrors the server's `absorb_over_settlements` — same order (largest short first, invoice
 * number breaking ties), same proportional floor plus a cent-at-a-time remainder — so the amounts
 * the page shows are the amounts the apply books. Mutates `rows` in place; `partial_cents` is never
 * touched, and an excess the shorts cannot absorb is left on the row so the gap still fails to
 * explain the balance delta.
 */
export function absorb_over_settlements(rows: SettlementBreakdownRow[]): void {
  const over_total = rows.reduce((sum, r) => sum + r.over_cents, 0)
  const short_total = rows.reduce((sum, r) => sum + r.short_cents, 0)
  const absorb = Math.min(over_total, short_total)
  if (absorb === 0) {
    return
  }
  const shorted = rows
    .filter((r) => r.short_cents > 0)
    .sort((a, b) => b.short_cents - a.short_cents || a.invoice_number.localeCompare(b.invoice_number))
  const shares = shorted.map((r) => Math.floor((r.short_cents * absorb) / short_total))
  let leftover = absorb - shares.reduce((sum, share) => sum + share, 0)
  while (leftover > 0) {
    let placed = false
    for (let k = 0; k < shorted.length && leftover > 0; k++) {
      if (shares[k] < shorted[k].short_cents) {
        shares[k] += 1
        leftover -= 1
        placed = true
      }
    }
    if (!placed) {
      break
    }
  }
  shorted.forEach((row: SettlementBreakdownRow, k: number) => {
    row.short_cents -= shares[k]
  })
}

/**
 * Widest per-invoice gap offered as an additional discount, as a share of the invoice total. Mirrors
 * the server's `MAX_ADDITIONAL_DISCOUNT_RATE`: a gap beyond it is a short pay, not a discount.
 */
export const MAX_ADDITIONAL_DISCOUNT_RATE = 0.05

/**
 * The settlement rows a non-Xero apply can close by booking each invoice's gap as an **additional
 * discount**, or `null` when the gap is not that shape: every row must be short (nothing sitting on
 * another check — a partial payment has no apply path outside Xero) and every short must be within
 * `MAX_ADDITIONAL_DISCOUNT_RATE` of its invoice's total. Walmart computes its 2% early pay off the
 * EDI subtotal before its own allowances, so its remittance discount under-states what it kept by a
 * few dollars per invoice (Super Coffee's 4393492: $21.29 across four of six invoices); that is the
 * gap this books, one credit memo line per invoice, so the payment can post.
 */
export function additional_discount_rows(
  settlement_rows: SettlementBreakdownRow[] | null,
  accounting_invoice_by_id: Map<string, { total_amount: number }>,
  assignments: Record<string, string>
): SettlementBreakdownRow[] | null {
  if (settlement_rows === null || settlement_rows.length === 0) {
    return null
  }
  // An invoice with nothing short needs no discount line: it was settled over its total, or its
  // short was fully absorbed by one that was. Skipped, not refused — the server's
  // `plan_additional_discount_lines` books only the rows that are still short.
  const rows = settlement_rows.filter((row) => row.short_cents > 0)
  if (rows.length === 0) {
    return null
  }
  for (const row of rows) {
    if (row.partial_cents > 0) {
      return null
    }
    const invoice = accounting_invoice_by_id.get(assignments[row.deduction_id] ?? "")
    if (!invoice) {
      return null
    }
    const cap_cents = Math.floor(dollars_to_cents(invoice.total_amount) * MAX_ADDITIONAL_DISCOUNT_RATE)
    if (row.short_cents > cap_cents) {
      return null
    }
  }
  return rows
}

/** One invoice whose "Disc. Taken" comes down so the check stops settling it past its total. */
export interface DiscountReductionRow {
  deduction_id: string
  invoice_number: string
  reduce_cents: number
}

/**
 * The mirror of `additional_discount_rows` for a check that settles its invoices **net over** their
 * totals, when early pay rides on the payment: how much less "Disc. Taken" to take on each
 * over-settled invoice, largest over first and never below zero. Mirrors the server's
 * `plan_payment_discount_reductions`. `null` unless the check is over (`delta_cents < 0`) by exactly
 * the per-invoice gaps and those invoices' discounts can absorb it. Walmart's check 4477247 settles
 * three invoices past their totals (each with a reason 100 write-off in the memo) and one $4.47
 * short, netting $1.39 over — nothing is short, so the additional-discount offer never appeared.
 */
export function payment_discount_reduction_rows(
  invoice_lines: { deduction_id: string; settle_amount?: number | null; early_pay_discount?: number | null }[],
  assignments: Record<string, string>,
  accounting_invoice_by_id: Map<string, { invoice_number: string; total_amount: number }>,
  delta_cents: number
): DiscountReductionRow[] | null {
  if (delta_cents >= 0) {
    return null
  }
  const by_invoice = new Map<string, { deduction_id: string; settled_cents: number; discount_cents: number }>()
  for (const line of invoice_lines) {
    const invoice_id = assignments[line.deduction_id] ?? ""
    if (!accounting_invoice_by_id.has(invoice_id) || line.settle_amount === undefined || line.settle_amount === null) {
      continue
    }
    const entry = by_invoice.get(invoice_id) ?? { deduction_id: line.deduction_id, settled_cents: 0, discount_cents: 0 }
    entry.settled_cents += dollars_to_cents(line.settle_amount)
    entry.discount_cents += dollars_to_cents(line.early_pay_discount ?? 0)
    by_invoice.set(invoice_id, entry)
  }
  let net_cents = 0
  const overs: { deduction_id: string; invoice_number: string; over_cents: number; discount_cents: number }[] = []
  for (const [invoice_id, entry] of by_invoice) {
    const invoice = accounting_invoice_by_id.get(invoice_id)!
    const gap_cents = dollars_to_cents(invoice.total_amount) - entry.settled_cents
    net_cents += gap_cents
    if (gap_cents < 0) {
      overs.push({ deduction_id: entry.deduction_id, invoice_number: invoice.invoice_number, over_cents: -gap_cents, discount_cents: entry.discount_cents })
    }
  }
  if (net_cents !== delta_cents) {
    return null
  }
  overs.sort((a, b) => b.over_cents - a.over_cents || a.invoice_number.localeCompare(b.invoice_number))
  let left_cents = -net_cents
  const rows: DiscountReductionRow[] = []
  for (const over of overs) {
    const take_cents = Math.min(left_cents, over.over_cents, Math.max(over.discount_cents, 0))
    if (take_cents > 0) {
      rows.push({ deduction_id: over.deduction_id, invoice_number: over.invoice_number, reduce_cents: take_cents })
      left_cents -= take_cents
    }
  }
  return left_cents === 0 && rows.length > 0 ? rows : null
}

/**
 * The early-pay discount on the remittance for one invoice line: what the check settles for it less
 * the cash. Mirrors the server's `invoice_discount_amount`. The preview only sends
 * `early_pay_discount` when early pay rides on the payment; in credit-memo mode this is the figure
 * `payment_discount_reduction_rows` caps each invoice's reduction at.
 */
export function remittance_early_pay_discount(line: { settle_amount?: number | null; deduction_amount: number }): number {
  if (line.settle_amount === undefined || line.settle_amount === null) {
    return 0
  }
  return Math.max(dollars_to_cents(line.settle_amount) - dollars_to_cents(line.deduction_amount), 0) / 100
}

/**
 * Credit-memo mode's version of `payment_discount_reduction_rows`: the negative credit memo lines
 * the server adds (`plan_discount_reduction_lines`) when the apply carries
 * `additional_discount_account_id` on a check settled net over its invoices. Same account,
 * description and amounts as the server, so the memo table and the balance show what posts.
 */
export function build_discount_reduction_lines(
  rows: DiscountReductionRow[],
  account: Pick<LedgerAccountJson, "account_number" | "expense_account" | "product_service">
): CreditMemoPreviewLine[] {
  return rows.map((row) => ({
    gl_account_id: account.account_number,
    gl_account_name: account.expense_account,
    amount: -row.reduce_cents / 100,
    description: `Early-pay discount not taken - ${row.invoice_number}`,
    product_service: account.product_service ?? undefined,
  }))
}

/**
 * Accounting systems whose apply can settle an invoice for **less than its total**, leaving the
 * remainder due: a short pay (money the customer kept) or a later installment of an invoice paid
 * across several checks. Mirrors the server's `partial_settlement_supported` — Xero allocates cash
 * and credit notes invoice by invoice and a NetSuite customer payment's `apply` sublist takes any
 * amount up to the invoice's balance, and a Sage Intacct AR payment line pays any amount of its
 * invoice (the multi-DC tail settles each DC's invoices the same way). QuickBooks has no such path yet.
 */
export function supports_partial_settlement(accounting_system: string): boolean {
  return accounting_system === "Xero" || accounting_system === "NetSuite" || accounting_system === "Sage Intacct"
}

/**
 * The apply page offers at most **one** way to close a per-invoice gap, and this picks it: the
 * settlement rows to offer as a short pay, or `null` when short pay is not the right offer.
 *
 * Within the additional-discount band the discount wins — it closes every invoice in full, which is
 * the better outcome (Walmart's 2% taken off the pre-allowance subtotal). Beyond the band the gap is
 * money the customer kept and must be flagged rather than written off: Walmart discounts the whole
 * PO but pays for what it received, so Super Coffee's check 4345810 is short $66.91 across three
 * invoices at 5.6%–12.5% each. Xero books no additional discount at all, so it always lands here.
 */
export function short_pay_offer_rows(
  settlement_breakdown: SettlementBreakdownRow[] | null,
  discount_rows: SettlementBreakdownRow[] | null,
  accounting_system: string
): SettlementBreakdownRow[] | null {
  if (discount_rows !== null || !supports_partial_settlement(accounting_system)) {
    return null
  }
  return settlement_breakdown
}

/**
 * Whether the short-pay offer can book the shorts as open deductions (`short_pay_as_deduction`)
 * instead of leaving them due on the invoices. Mirrors the server's refusals: it needs the
 * open-deductions holding account, a single-customer apply, no KeHE BI allowance lines, and every
 * gap on the check to be a plain short — an invoice also paid on another check or already credited
 * keeps the flag-only path.
 */
export function can_book_short_pay_deduction(
  settlement_rows: SettlementBreakdownRow[] | null,
  options: { open_deductions_account: string | null | undefined; split_mode: boolean; bi_allowance_line_count: number }
): boolean {
  if (settlement_rows === null || !settlement_rows.some((row: SettlementBreakdownRow) => row.short_cents > 0)) {
    return false
  }
  if (!options.open_deductions_account || options.split_mode || options.bi_allowance_line_count > 0) {
    return false
  }
  return settlement_rows.every((row: SettlementBreakdownRow) => row.partial_cents === 0 && row.credited_cents === 0)
}

/**
 * The credit memo lines the server will add for `additional_discount_rows` when the apply carries
 * `additional_discount_account_id` — shown on the preview so the memo table and the balance math
 * reflect exactly what posts. Same account, description and per-invoice amounts as the server.
 */
export function build_additional_discount_lines(
  rows: SettlementBreakdownRow[],
  account: Pick<LedgerAccountJson, "account_number" | "expense_account" | "product_service">
): CreditMemoPreviewLine[] {
  return rows.map((row) => ({
    gl_account_id: account.account_number,
    gl_account_name: account.expense_account,
    amount: row.short_cents / 100,
    description: `Additional early-pay discount - ${row.invoice_number}`,
    product_service: account.product_service ?? undefined,
  }))
}

/** KeHE's BI allowance on an invoice of `total_cents` at `percentage` percent, to the cent. */
export function bi_allowance_cents(total_cents: number, percentage: number): number {
  return Math.round((total_cents * percentage) / 100)
}

/**
 * How much of an invoice's short is KeHE's BI allowance: all of it within a cent of the expected
 * allowance, the allowance alone when the short is wider (the rest stays a short pay), nothing when
 * it is narrower. Mirrors the server's `bi_allowance_booking_cents`.
 */
export function bi_allowance_booking_cents(short_cents: number, expected_cents: number): number {
  if (short_cents <= 0 || expected_cents <= 0) {
    return 0
  }
  if (Math.abs(short_cents - expected_cents) <= 1) {
    return short_cents
  }
  return short_cents > expected_cents ? expected_cents : 0
}

/** One invoice's BI allowance, written off on the credit memo. */
export interface BiAllowanceRow {
  deduction_id: string
  invoice_number: string
  booked_cents: number
}

/**
 * Write KeHE's BI allowance off each short invoice before any short pay or additional discount is
 * offered. KeHE pays every invoice less its BI rate and never lists it on the remittance
 * (Compostic's SI-00004155: $2,893.44 in Xero, $2,835.57 settled, $57.87 = 2% kept). Returns the
 * rows booked and the breakdown with each matched short reduced by what was booked, so the rest of
 * the page only sees what is left. Mirrors the server's `plan_bi_allowance_lines`.
 */
export function plan_bi_allowance_rows(
  breakdown: SettlementBreakdownRow[] | null,
  accounting_invoice_by_id: Map<string, { total_amount: number }>,
  assignments: Record<string, string>,
  percentage: number
): { rows: BiAllowanceRow[]; remaining: SettlementBreakdownRow[] | null } {
  if (breakdown === null) {
    return { rows: [], remaining: null }
  }
  const rows: BiAllowanceRow[] = []
  const remaining = breakdown.map((row: SettlementBreakdownRow) => {
    const invoice = accounting_invoice_by_id.get(assignments[row.deduction_id] ?? "")
    if (!invoice || row.short_cents <= 0) {
      return row
    }
    const booked = bi_allowance_booking_cents(row.short_cents, bi_allowance_cents(dollars_to_cents(invoice.total_amount), percentage))
    if (booked === 0) {
      return row
    }
    rows.push({ deduction_id: row.deduction_id, invoice_number: row.invoice_number, booked_cents: booked })
    return { ...row, short_cents: row.short_cents - booked }
  })
  return { rows, remaining }
}

/** The allowance's name on memo lines: KeHE's BI allowance unless the server names another payer's. */
export function bi_allowance_label(bi: BiAllowancePreview): string {
  return bi.label || "KeHE BI allowance"
}

/** The credit memo lines the server books for `plan_bi_allowance_rows` — same account, memo and amounts. */
export function build_bi_allowance_lines(rows: BiAllowanceRow[], bi: BiAllowancePreview): CreditMemoPreviewLine[] {
  return rows.map((row: BiAllowanceRow) => ({
    gl_account_id: bi.gl_account_id,
    gl_account_name: bi.gl_account_name,
    amount: row.booked_cents / 100,
    description: `${bi_allowance_label(bi)} - ${row.invoice_number}`,
    reference: row.invoice_number,
    product_service: bi.product_service ?? undefined,
  }))
}

/** Tells the user where the BI-sized shorts went, since the remittance never mentions them. */
export function build_bi_allowance_note(rows: BiAllowanceRow[], bi: BiAllowancePreview): string | null {
  if (rows.length === 0) {
    return null
  }
  const total_cents = rows.reduce((sum: number, row: BiAllowanceRow) => sum + row.booked_cents, 0)
  const detail = rows.map((row: BiAllowanceRow) => `${exactCurrencyFormatter(row.booked_cents / 100)} on ${row.invoice_number}`).join(", ")
  // Walmart books each invoice net of its supplier-agreement allowance and pays that figure, so
  // the remittance shows no discount at all — same shape as KeHE's BI, under the payer's own name.
  const label = bi_allowance_label(bi)
  const lead =
    label === "KeHE BI allowance"
      ? `KeHE keeps its ${bi.percentage}% BI allowance from each invoice without listing it on the remittance. `
      : `${label}: ${bi.percentage}% of each invoice is kept without being listed on the remittance. `
  return (
    lead +
    `${exactCurrencyFormatter(total_cents / 100)} (${detail}) is written off to ${bi.gl_account_id} ${bi.gl_account_name} on the credit memo.`
  )
}

/**
 * A plain-language note for invoices this check settles *beyond* their total, and where the excess
 * went. Informational — there is nothing to confirm — but never silent: the shorts listed elsewhere
 * on the page are net of it (`absorb_over_settlements`), so anyone reconciling against the
 * remittance has to be able to see the difference. Walmart's reason 100 "debit balance write off"
 * is the common source: its own invoice figure runs a hair above ours, it pays its number, then
 * trues the residual up with a separate adjustment line.
 */
export function build_over_settlement_note(rows: SettlementBreakdownRow[]): string | null {
  const over = rows.filter((r) => r.over_cents > 0)
  if (over.length === 0) {
    return null
  }
  const total_cents = over.reduce((sum, r) => sum + r.over_cents, 0)
  const detail = over.map((r) => `${r.invoice_number} by ${exactCurrencyFormatter(r.over_cents / 100)}`).join(", ")
  return (
    `This check settles ${over.length === 1 ? "one invoice" : `${over.length} invoices`} past its total — ${detail}. ` +
    `That ${exactCurrencyFormatter(total_cents / 100)} is applied to the other invoices on the check rather than left as ` +
    `unapplied cash, so any short pay shown below is net of it.`
  )
}

/**
 * A plain-language note for the invoices this check pays only part of, the rest being on other
 * checks. Informational — nothing to confirm — so the user isn't asked to "apply anyway" for money
 * that is sitting on a check they can see.
 */
export function build_partial_payment_note(rows: SettlementBreakdownRow[], accounting_system: string): string | null {
  const partial = rows.filter((r) => r.partial_cents > 0)
  if (partial.length === 0) {
    return null
  }
  const parts = partial.map((r) => {
    const checks =
      r.partial_check_numbers.length > 0
        ? `check${r.partial_check_numbers.length === 1 ? "" : "s"} ${r.partial_check_numbers.join(", ")}`
        : "another check"
    return (
      `${r.invoice_number}: this check settles ${exactCurrencyFormatter(r.settled_cents / 100)} of it; ` +
      `${exactCurrencyFormatter(r.partial_cents / 100)} is on ${checks}`
    )
  })
  return (
    `${partial.length === 1 ? "One invoice is" : `${partial.length} invoices are`} paid across several checks — ` +
    `${parts.join("; ")}. The rest stays due in ${accounting_system} until ${partial.length === 1 && partial[0].partial_check_numbers.length === 1 ? "that check is" : "those checks are"} applied.`
  )
}

/** One selected invoice that can't take the full payment because it's already (partly) paid. */
export interface InvoiceOutstandingIssue {
  invoice_number: string
  outstanding_cents: number
  charge_cents: number
}

/**
 * Selected invoices whose remaining balance in the accounting system is less than the amount this
 * cash application would apply (the invoice total). Xero and NetSuite reject applying more than an
 * invoice's outstanding balance, so these are blocked before applying — mirroring the server guard.
 * Invoices with no synced `amount_due` (never synced) can't be judged and are skipped.
 */
export function find_overpaid_invoices(
  selected: { id?: string; invoice_number: string; total_amount: number; amount_due?: number | null }[],
  credited_by_invoice: Map<string, number> = new Map()
): InvoiceOutstandingIssue[] {
  const issues: InvoiceOutstandingIssue[] = []
  for (const inv of selected) {
    if (inv.amount_due === undefined || inv.amount_due === null) {
      continue
    }
    const outstanding_cents = Math.round(inv.amount_due * 100)
    // The apply charges the total less what the system already applied as a credit note the check
    // lists — so an invoice short of its total by exactly that credit is not over-applied.
    const charge_cents = Math.round(inv.total_amount * 100) - Math.max(credited_by_invoice.get(inv.id ?? "") ?? 0, 0)
    if (outstanding_cents < charge_cents) {
      issues.push({ invoice_number: inv.invoice_number, outstanding_cents, charge_cents })
    }
  }
  return issues
}

/** The preview's `already_credited`, as the helpers below need it. */
export interface AlreadyCreditedRow {
  /** `null` for a credit note the remittance nets off without listing it. */
  deduction_id: string | null
  invoice_number: string | null
  amount: number | string
  accounting_invoice_id: string
  accounting_invoice_number: string
  credit_note_number: string
}

/**
 * Cents already applied per accounting invoice, from the preview's `already_credited`: what each
 * selected invoice is charged *less* than its total, and what its credit-note deductions would
 * have credited a second time. Mirrors the server's `credited_by_invoice`.
 */
export function credited_by_invoice_from_preview(rows: AlreadyCreditedRow[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const row of rows) {
    const cents = dollars_to_cents(Number(row.amount))
    if (!Number.isFinite(cents) || cents <= 0) {
      continue
    }
    out.set(row.accounting_invoice_id, (out.get(row.accounting_invoice_id) ?? 0) + cents)
  }
  return out
}

/** Dollars to whole cents; a missing amount is zero. */
export function amount_to_cents(amount: number | undefined): number {
  return Math.round((amount ?? 0) * 100)
}

/**
 * What this apply charges an invoice: its total, less any credit note the accounting system has
 * already applied to it that the check also lists (`already_credited`) — the balance actually due.
 */
export function invoice_charge_cents(invoice: AccountingInvoiceJson | undefined, credited_by_invoice: Map<string, number>): number {
  if (!invoice) {
    return 0
  }
  return amount_to_cents(invoice.total_amount) - Math.max(credited_by_invoice.get(invoice.id) ?? 0, 0)
}

/**
 * Whether an invoice line's cash may be left unapplied on the customer's account instead of being
 * matched to an invoice. Mirrors the server's `plan_unapplied_cash`: the provider must carry
 * unapplied cash on a payment (`unapplied_cash_supported`), the line must carry cash, and where the
 * org books early-pay discounts on the credit memo a discounted line is out — its discount would
 * reach the memo with no invoice to offset.
 */
export function can_leave_line_unapplied(
  preview: Pick<CashApplicationPreview, "unapplied_cash_supported" | "early_pay_on_payment">,
  line: Pick<InvoiceAssignmentLine, "kind" | "deduction_amount" | "settle_amount">
): boolean {
  if (preview.unapplied_cash_supported !== true || line.kind !== "invoice") {
    return false
  }
  const cash_cents = amount_to_cents(line.deduction_amount)
  if (cash_cents <= 0) {
    return false
  }
  const discount_cents = Math.max(amount_to_cents(line.settle_amount ?? line.deduction_amount) - cash_cents, 0)
  return preview.early_pay_on_payment === true || discount_cents === 0
}

/**
 * What the page says about the cash it will leave unapplied: how much, from which lines, and where
 * it ends up. `null` when nothing is left unapplied.
 */
export function build_unapplied_cash_note(
  lines: Pick<InvoiceAssignmentLine, "invoice_number" | "deduction_amount">[],
  check_cash_cents: number,
  accounting_system: string
): string | null {
  if (lines.length === 0) {
    return null
  }
  const total_cents = lines.reduce((sum: number, line) => sum + amount_to_cents(line.deduction_amount), 0)
  const detail = lines.map((line) => `${line.invoice_number} (${exactCurrencyFormatter(line.deduction_amount)})`).join(", ")
  return (
    `Unapplied: ${exactCurrencyFormatter(total_cents / 100)} stays on the customer's account in ${accounting_system} — ${detail}. ` +
    `The payment is created for the full ${exactCurrencyFormatter(check_cash_cents / 100)}; ` +
    `${exactCurrencyFormatter((check_cash_cents - total_cents) / 100)} of it settles the selected invoices and the rest is left as an ` +
    `unapplied balance to apply or refund later in ${accounting_system}.`
  )
}

/** The two sides of an apply, in cents, for one set of invoice assignments. */
export interface ApplyBalance {
  /**
   * The preview's invoice-kind lines that need an accounting invoice — every one except those left
   * unapplied on the customer's account.
   */
  invoice_lines: InvoiceAssignmentLine[]
  /** The invoice-kind lines left unapplied (`unapplied_ids`), in the preview's order. */
  unapplied_lines: InvoiceAssignmentLine[]
  unassigned_count: number
  accounting_invoice_by_id: Map<string, AccountingInvoiceJson>
  credited_by_invoice: Map<string, number>
  selected_invoice_total_cents: number
  check_cash_cents: number
  coded_credit_memo_total_cents: number
  early_pay_discount_cents: number
  /**
   * Cash of the invoice lines left unapplied on the customer's account (`unapplied_ids`). Still part
   * of the payment, but it settles no invoice, so it comes off the applied side.
   */
  unapplied_cash_cents: number
  /** Cash (less what stays unapplied) + coded credit memo + early-pay discounts taken on the payment. */
  base_applied_total_cents: number
  /** `selected_invoice_total_cents - base_applied_total_cents`; zero when the sides balance. */
  payment_balance_delta_cents: number
}

/**
 * Whether an apply balances: the invoices chosen for the check's invoice lines against the cash,
 * the coded credit memo and any early-pay discount taken on the payment. One rule for the Apply
 * Cash page and for applying checks in bulk, so a check the page calls balanced is one the bulk
 * run will post, and the other way round.
 */
export function compute_apply_balance(
  preview: Pick<CashApplicationPreview, "invoice_assignments" | "credit_memo_lines" | "check_amount" | "already_credited">,
  assignments: Record<string, string>,
  accounting_invoices: AccountingInvoiceJson[],
  unapplied_ids: ReadonlySet<string> = new Set()
): ApplyBalance {
  const all_invoice_lines = preview.invoice_assignments.filter((l: InvoiceAssignmentLine) => l.kind === "invoice")
  // A line left unapplied needs no invoice and settles none: it is out of every sum below except
  // the cash it takes away from the invoices. Any invoice still picked for it is ignored.
  const invoice_lines = all_invoice_lines.filter((l: InvoiceAssignmentLine) => !unapplied_ids.has(l.deduction_id))
  const unapplied_lines = all_invoice_lines.filter((l: InvoiceAssignmentLine) => unapplied_ids.has(l.deduction_id))
  const unapplied_cash_cents = unapplied_lines.reduce(
    (sum: number, l: InvoiceAssignmentLine) => sum + amount_to_cents(l.deduction_amount),
    0
  )
  const unassigned_count = invoice_lines.filter((l: InvoiceAssignmentLine) => !assignments[l.deduction_id]).length
  const accounting_invoice_by_id = new Map<string, AccountingInvoiceJson>(
    accounting_invoices.map((invoice: AccountingInvoiceJson) => [invoice.id, invoice])
  )
  const credited_by_invoice = credited_by_invoice_from_preview(preview.already_credited ?? [])
  // Each invoice is charged once, however many lines of the check pay it (UNFI's SI00004292 +
  // SI00004292A both settle Compostic's SI-00004292) — the server loads the picked invoices by id.
  const selected_invoice_ids = new Set(
    invoice_lines.map((line: InvoiceAssignmentLine) => assignments[line.deduction_id]).filter((id): id is string => !!id)
  )
  const selected_invoice_total_cents = [...selected_invoice_ids].reduce(
    (sum: number, invoice_id: string) => sum + invoice_charge_cents(accounting_invoice_by_id.get(invoice_id), credited_by_invoice),
    0
  )
  const check_cash_cents = amount_to_cents(preview.check_amount)
  const coded_credit_memo_total_cents = preview.credit_memo_lines.reduce(
    (sum: number, line: CreditMemoPreviewLine) => sum + amount_to_cents(line.amount),
    0
  )
  // Early-pay discounts taken on the payment itself ("Disc. Taken") settle part of each gross
  // invoice, so they fund the application alongside cash and the credit memo (which excludes them
  // in this mode). Only assigned invoice lines count — the assignment is editable on the page.
  const early_pay_discount_cents = invoice_lines.reduce((sum: number, line: InvoiceAssignmentLine) => {
    if (!assignments[line.deduction_id]) return sum
    return sum + amount_to_cents(line.early_pay_discount ?? 0)
  }, 0)
  const base_applied_total_cents = check_cash_cents - unapplied_cash_cents + coded_credit_memo_total_cents + early_pay_discount_cents
  return {
    invoice_lines,
    unapplied_lines,
    unassigned_count,
    accounting_invoice_by_id,
    credited_by_invoice,
    selected_invoice_total_cents,
    check_cash_cents,
    coded_credit_memo_total_cents,
    early_pay_discount_cents,
    unapplied_cash_cents,
    base_applied_total_cents,
    payment_balance_delta_cents: selected_invoice_total_cents - base_applied_total_cents,
  }
}

/**
 * Tells the user which remittance lines are missing from the tables and why: the accounting system
 * already applied them, so they are neither credited again nor charged. Never silent — the user is
 * holding a remittance that lists them.
 */
export function build_already_credited_note(rows: AlreadyCreditedRow[], accounting_system: string): string | null {
  if (rows.length === 0) {
    return null
  }
  const total_cents = rows.reduce((sum: number, row: AlreadyCreditedRow) => sum + dollars_to_cents(Number(row.amount)), 0)
  const listed = rows.filter((row: AlreadyCreditedRow) => row.invoice_number !== null)
  const unlisted = rows.filter((row: AlreadyCreditedRow) => row.invoice_number === null)
  const sentences: string[] = []
  if (listed.length > 0) {
    const detail = listed
      .map(
        (row: AlreadyCreditedRow) =>
          `${row.invoice_number} (${exactCurrencyFormatter(Number(row.amount))}, applied to ${row.accounting_invoice_number} as ${row.credit_note_number})`
      )
      .join("; ")
    const subject = listed.length === 1 ? "One line on this remittance is" : `${listed.length} lines on this remittance are`
    sentences.push(`${subject} already applied in ${accounting_system}: ${detail}.`)
  }
  if (unlisted.length > 0) {
    // Thrive nets its monthly brand funding off invoices without printing a line for it.
    const detail = unlisted
      .map(
        (row: AlreadyCreditedRow) =>
          `${row.credit_note_number} (${exactCurrencyFormatter(Number(row.amount))}, applied to ${row.accounting_invoice_number})`
      )
      .join("; ")
    const subject = unlisted.length === 1 ? "A credit note already applied" : `${unlisted.length} credit notes already applied`
    sentences.push(
      `${subject} in ${accounting_system} ${unlisted.length === 1 ? "covers" : "cover"} what the customer netted off this ` +
        `payment without listing it: ${detail}. The check is short of its remittance lines by exactly that amount.`
    )
  }
  sentences.push(
    `${exactCurrencyFormatter(total_cents / 100)} is left off the credit memo, and each invoice is applied for its total less ` +
      `what its credit note already settled — the balance ${accounting_system} has due.`
  )
  return sentences.join(" ")
}

/** Message for selected invoices that are already (partly) paid and can't take the full payment. */
export function build_overpaid_invoices_error(issues: InvoiceOutstandingIssue[], accounting_system: string): string {
  const details = issues
    .map(
      (i) =>
        `${i.invoice_number} (${exactCurrencyFormatter(i.outstanding_cents / 100)} left of ${exactCurrencyFormatter(
          i.charge_cents / 100
        )})`
    )
    .join("; ")
  return (
    `These invoices are already partly or fully paid in ${accounting_system}, so the payment can't be applied in full: ${details}. ` +
    `Re-match to open invoices, or settle them in ${accounting_system} first.`
  )
}

/**
 * Short outstanding-balance label for an accounting invoice in the picker, so a user doesn't select
 * one that can't take the payment. Returns `null` when the invoice is fully open (or never synced),
 * otherwise a "Paid" / "$X left" tag with the tone to render it in.
 */
export function invoice_outstanding_tag(
  inv: Pick<AccountingInvoiceJson, "total_amount" | "amount_due" | "status">
): { label: string; tone: "paid" | "partial" } | null {
  if (inv.amount_due === undefined || inv.amount_due === null) {
    return null
  }
  const outstanding_cents = Math.round(inv.amount_due * 100)
  const total_cents = Math.round(inv.total_amount * 100)
  if (outstanding_cents <= 0) {
    return { label: "Paid", tone: "paid" }
  }
  if (outstanding_cents < total_cents) {
    return { label: `${exactCurrencyFormatter(inv.amount_due)} left`, tone: "partial" }
  }
  return null
}

/** Splits that roll into a credit memo GL line (same rule as server: coded account only). */
export function collect_splits_for_gl_line(
  transactions: TransactionJson[],
  gl_account_id: string
): CreditMemoSplitContributor[] {
  const out: CreditMemoSplitContributor[] = []
  for (const t of transactions) {
    for (const s of t.splits) {
      if (!s.account_id) {
        continue
      }
      const num = s.account_number?.trim()
      if (!num || num !== gl_account_id) {
        continue
      }
      out.push({
        deduction_id: t.id,
        invoice_ref: t.invoice_number,
        kind: t.kind,
        amount: credit_memo_split_amount(t.invoice_amount, s.amount),
        memo: s.code_description,
        gl_account_number: num,
      })
    }
  }
  return out
}

export function format_split_gl_summary(s: Split): string {
  const gl = s.account_number?.trim() || "Uncoded"
  const label = s.expense_account?.trim() || s.name?.trim() || ""
  if (label && label !== gl) {
    return `${gl} — ${label}`
  }
  return gl
}

export interface QbCustomerRecommendationContext {
  customer_name_hint?: string
  deduction_source?: string
  customer_remote_id?: string
}

export type QbCustomerPick = Pick<QbCustomer, "remote_id" | "display_name">

function normalize_customer_name_for_match(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ")
}

function customer_name_alphanumeric_only(raw: string): string {
  return raw.replace(/[^a-z0-9]/gi, "").toLowerCase()
}

const QB_CUSTOMER_SOURCE_MATCH_SCORE = 3_000_000
const QB_CUSTOMER_CLEAR_WINNER_MARGIN = 500_000

/** Higher = better match. Deterministic for same inputs. */
export function score_qb_customer_match(
  context: QbCustomerRecommendationContext,
  customer: QbCustomerPick
): number {
  if (context.customer_remote_id && context.customer_remote_id === customer.remote_id) {
    return 5_000_000
  }

  const hint = context.customer_name_hint?.trim()
  let stringScore = 0
  if (hint) {
    const left = normalize_customer_name_for_match(hint)
    const right = normalize_customer_name_for_match(customer.display_name)
    const leftAlnum = customer_name_alphanumeric_only(hint)
    const rightAlnum = customer_name_alphanumeric_only(customer.display_name)
    if (left.length > 0 && right.length > 0) {
      if (left === right) {
        stringScore = 4_000_000
      } else if (leftAlnum.length > 0 && leftAlnum === rightAlnum) {
        stringScore = 3_200_000
      } else if (left.includes(right) || right.includes(left)) {
        stringScore = 1_600_000
      } else if (
        leftAlnum.length >= 4 &&
        rightAlnum.length >= 4 &&
        (leftAlnum.includes(rightAlnum) || rightAlnum.includes(leftAlnum))
      ) {
        stringScore = 800_000
      }
    }
  }

  const sourceScore =
    score_customer_for_deduction_source(customer.display_name, context.deduction_source) >= 3
      ? QB_CUSTOMER_SOURCE_MATCH_SCORE
      : 0

  return stringScore + sourceScore
}

export function compare_qb_customers_by_recommendation(
  a: QbCustomerPick,
  b: QbCustomerPick,
  context: QbCustomerRecommendationContext
): number {
  const sa = score_qb_customer_match(context, a)
  const sb = score_qb_customer_match(context, b)
  if (sb !== sa) {
    return sb - sa
  }
  return a.display_name.localeCompare(b.display_name)
}

export function sort_qb_customers_by_recommendation(
  customers: QbCustomerPick[],
  context: QbCustomerRecommendationContext
): QbCustomerPick[] {
  return [...customers].sort((a, b) => compare_qb_customers_by_recommendation(a, b, context))
}

function customer_name_hint_matches(customer_name_hint: string, display_name: string): boolean {
  const left = normalize_customer_name_for_match(customer_name_hint)
  const right = normalize_customer_name_for_match(display_name)
  const leftAlnum = customer_name_alphanumeric_only(customer_name_hint)
  const rightAlnum = customer_name_alphanumeric_only(display_name)
  return left === right || (leftAlnum.length > 0 && leftAlnum === rightAlnum)
}

/** Conservative auto-assign gate for the QuickBooks customer picker on Apply Cash. */
export function find_auto_assigned_qb_customer(
  context: QbCustomerRecommendationContext,
  customers: QbCustomerPick[]
): QbCustomerPick | null {
  if (customers.length === 0) {
    return null
  }

  if (context.customer_remote_id) {
    const by_id = customers.find((customer) => customer.remote_id === context.customer_remote_id)
    if (by_id) {
      return by_id
    }
  }

  const scored = customers
    .map((customer) => ({ customer, score: score_qb_customer_match(context, customer) }))
    .sort((a, b) => compare_qb_customers_by_recommendation(a.customer, b.customer, context))

  const top = scored[0]
  const runner_up = scored[1]
  if (!top || top.score <= 0) {
    return null
  }

  const hint = context.customer_name_hint?.trim()
  if (hint && customer_name_hint_matches(hint, top.customer.display_name)) {
    return top.customer
  }

  const source_matches = scored.filter(
    (entry) => score_customer_for_deduction_source(entry.customer.display_name, context.deduction_source) >= 3
  )
  if (source_matches.length === 1 && source_matches[0].customer.remote_id === top.customer.remote_id) {
    return top.customer
  }

  const runner_up_score = runner_up?.score ?? 0
  if (
    top.score >= QB_CUSTOMER_SOURCE_MATCH_SCORE &&
    top.score - runner_up_score >= QB_CUSTOMER_CLEAR_WINNER_MARGIN
  ) {
    return top.customer
  }

  return null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("normalize_invoice_number_for_match", () => {
    it("trims and lowercases", () => {
      expect(normalize_invoice_number_for_match("  INV-1  ")).toBe("inv-1")
    })
  })

  describe("score_accounting_invoice_match", () => {
    it("ranks exact invoice number highest among options", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV03973", amount_dollars: 100 }
      const exact = score_accounting_invoice_match(ctx, {
        id: "a",
        invoice_number: "INV03973",
        total_amount: 999,
        invoice_date: "2020-01-01",
      })
      const wrongNum = score_accounting_invoice_match(ctx, {
        id: "b",
        invoice_number: "OTHER",
        total_amount: 100,
        invoice_date: "2025-01-01",
      })
      expect(exact).toBeGreaterThan(wrongNum)
    })

    it("boosts amount when numbers differ", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "X", amount_dollars: 50 }
      const close = score_accounting_invoice_match(ctx, {
        id: "1",
        invoice_number: "Y",
        total_amount: 50,
      })
      const far = score_accounting_invoice_match(ctx, {
        id: "2",
        invoice_number: "Y",
        total_amount: 5000,
      })
      expect(close).toBeGreaterThan(far)
    })

    it("matches alphanumeric-only invoice numbers", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV-03973", amount_dollars: 0 }
      const s = score_accounting_invoice_match(ctx, {
        id: "1",
        invoice_number: "INV03973",
        total_amount: 0,
      })
      expect(s).toBeGreaterThanOrEqual(3_200_000)
    })
  })

  describe("should_auto_assign_accounting_invoice", () => {
    it("returns true for exact normalized invoice match", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "  inv-1 ", amount_dollars: 12.34 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "INV-1",
          total_amount: 99,
        })
      ).toBe(true)
    })

    it("returns true for exact alphanumeric core match when punctuation differs", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV-03973", amount_dollars: 50 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "INV03973",
          total_amount: 12,
        })
      ).toBe(true)
    })

    it("accepts fuzzy-close invoice number when amount is within min($1, 1%) band", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV-03973", amount_dollars: 1000 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "INV-03972",
          total_amount: 1000.25,
        })
      ).toBe(true)
    })

    it("rejects fuzzy-close invoice number when amount is outside the band", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV-03973", amount_dollars: 1000 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "INV-03972",
          total_amount: 1002,
        })
      ).toBe(false)
    })

    it("rejects distant invoice numbers even when amount is within the band", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV-03973", amount_dollars: 100 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "INV-99999",
          total_amount: 99.85,
        })
      ).toBe(false)
    })

    it("returns false for weak match", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "A", amount_dollars: 1 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "B",
          total_amount: 2,
        })
      ).toBe(false)
    })

    it("auto-assigns across a Xero doc-type prefix even when the amount differs", () => {
      // Our deduction "US-001234" vs Xero sales invoice "SI US-001234"; the amount differs by the
      // early-pay discount, which must not block the match.
      const ctx: InvoiceRecommendationContext = { invoice_number: "US-001234", amount_dollars: 124.16 }
      expect(
        should_auto_assign_accounting_invoice(ctx, {
          id: "x",
          invoice_number: "SI US-001234",
          total_amount: 130.0,
        })
      ).toBe(true)
    })
  })

  describe("invoice_prefix_only_match", () => {
    it("matches a Xero SI-prefixed sales invoice to our bare number", () => {
      expect(invoice_prefix_only_match("US-001234", "SI US-001234")).toBe(true)
      expect(invoice_prefix_only_match("SI US-001234", "US-001234")).toBe(true)
    })

    it("matches other doc-type prefixes (INV, BILL)", () => {
      expect(invoice_prefix_only_match("0364121X", "INV 0364121X")).toBe(true)
      expect(invoice_prefix_only_match("RPKP412488", "BILL RPKP412488")).toBe(true)
    })

    it("does not link short cores", () => {
      // Core "12" is too short to safely treat a prefix difference as a match.
      expect(invoice_prefix_only_match("12", "SI 12")).toBe(false)
    })

    it("is false for exact matches (handled by a higher tier) and unrelated numbers", () => {
      expect(invoice_prefix_only_match("US-001234", "US-001234")).toBe(false)
      expect(invoice_prefix_only_match("US-001234", "SI US-009999")).toBe(false)
    })

    it("does not strip part of the real number that lacks a separating space", () => {
      // "US" here is part of the number (no whitespace after), so these must not match.
      expect(invoice_prefix_only_match("US001234", "XX-009999")).toBe(false)
    })

    it("scores a prefixed match in the Recommended band", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "US-001234", amount_dollars: 124.16 }
      const s = score_accounting_invoice_match(ctx, {
        id: "x",
        invoice_number: "SI US-001234",
        total_amount: 130.0,
      })
      expect(s).toBeGreaterThanOrEqual(RECOMMENDED_INVOICE_BADGE_MIN_SCORE)
    })
  })

  describe("invoice_bare_number_match", () => {
    it("matches Walmart's bare number to NetSuite's INV-prefixed invoice, whatever the amount", () => {
      expect(invoice_bare_number_match("33259", "INV33259")).toBe(true)
      expect(invoice_bare_number_match("INV33259", "33259")).toBe(true)
      // Super Coffee's 4393492: cash 407.37 vs NetSuite 416.57 — the amount-gated rule never fired.
      const ctx: InvoiceRecommendationContext = { invoice_number: "33259", amount_dollars: 407.37 }
      const inv = { id: "x", invoice_number: "INV33259", total_amount: 416.57, invoice_date: "2026-08-28" }
      expect(should_auto_assign_accounting_invoice(ctx, inv as AccountingInvoiceJson)).toBe(true)
      expect(score_accounting_invoice_match(ctx, inv)).toBeGreaterThanOrEqual(RECOMMENDED_INVOICE_BADGE_MIN_SCORE)
    })

    it("drops UNFI's per-line suffix so a portal line meets its invoice", () => {
      expect(invoice_bare_number_match("INV33141-0-East", "INV33141")).toBe(true)
      expect(invoice_bare_number_match("INV33271-0-East", "INV33271")).toBe(true)
      expect(invoice_bare_number_match("1121763-2-West", "INV1121763")).toBe(true)
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV33271-0-East", amount_dollars: 7326.01 }
      const inv = { id: "x", invoice_number: "INV33271", total_amount: 7488.0, invoice_date: "2026-08-24" }
      expect(should_auto_assign_accounting_invoice(ctx, inv as AccountingInvoiceJson)).toBe(true)
    })

    it("does not link short cores, different numbers, or exact matches", () => {
      expect(invoice_bare_number_match("259", "INV259")).toBe(false)
      expect(invoice_bare_number_match("33259", "INV33260")).toBe(false)
      expect(invoice_bare_number_match("INV33259", "INV33259")).toBe(false)
      // A prefix only comes off when digits follow it; "USA" here is the number, not a doc type.
      expect(invoice_bare_number_match("USA-4412", "4412")).toBe(true)
      expect(invoice_bare_number_match("33259PP", "INV33259")).toBe(false)
    })

    it("seeds the Walmart lines on the apply page", () => {
      const lines = [
        { deduction_id: "d1", invoice_number: "33259", deduction_amount: 407.37 },
        { deduction_id: "d2", invoice_number: "33358", deduction_amount: 171.14 },
      ]
      const invoices = [
        { id: "i1", invoice_number: "INV33259", total_amount: 416.57, invoice_date: "2026-08-28" },
        { id: "i2", invoice_number: "INV33358", total_amount: 175.54, invoice_date: "2026-09-02" },
        { id: "i3", invoice_number: "INV33359", total_amount: 407.37, invoice_date: "2026-09-02" },
      ] as AccountingInvoiceJson[]
      expect(seed_auto_assignments_for_apply_page(lines, invoices, {})).toEqual({ d1: "i1", d2: "i2" })
    })
  })

  describe("payment_discount_reduction_rows", () => {
    // Super Coffee's Walmart check 4477247: three invoices settled past their totals, one short.
    const invoices = new Map<string, { invoice_number: string; total_amount: number }>([
      ["i13", { invoice_number: "INV33513", total_amount: 482.35 }],
      ["i19", { invoice_number: "INV33519", total_amount: 482.35 }],
      ["i43", { invoice_number: "INV33543", total_amount: 1447.03 }],
      ["i44", { invoice_number: "INV33544", total_amount: 25.07 }],
    ])
    const assignments = { d13: "i13", d19: "i19", d43: "i43", d44: "i44", ded: "" }
    const lines = [
      { deduction_id: "d13", settle_amount: 483.52, early_pay_discount: 9.77 },
      { deduction_id: "d19", settle_amount: 483.52, early_pay_discount: 9.77 },
      { deduction_id: "d43", settle_amount: 1450.55, early_pay_discount: 29.3 },
      { deduction_id: "d44", settle_amount: 20.6, early_pay_discount: 0.52 },
      { deduction_id: "ded" },
    ]

    it("takes the check's excess off the largest over-settled invoice's discount", () => {
      expect(payment_discount_reduction_rows(lines, assignments, invoices, -139)).toEqual([
        { deduction_id: "d43", invoice_number: "INV33543", reduce_cents: 139 },
      ])
    })

    it("spreads the excess when one discount can't carry it", () => {
      const small = lines.map((line) => (line.deduction_id === "d43" ? { ...line, early_pay_discount: 1 } : line))
      expect(payment_discount_reduction_rows(small, assignments, invoices, -139)).toEqual([
        { deduction_id: "d43", invoice_number: "INV33543", reduce_cents: 100 },
        { deduction_id: "d13", invoice_number: "INV33513", reduce_cents: 39 },
      ])
    })

    it("offers nothing unless the check is over by exactly the per-invoice gaps", () => {
      expect(payment_discount_reduction_rows(lines, assignments, invoices, 139)).toBeNull()
      expect(payment_discount_reduction_rows(lines, assignments, invoices, 0)).toBeNull()
      expect(payment_discount_reduction_rows(lines, assignments, invoices, -200)).toBeNull()
      const no_discount = lines.map((line) => ({ ...line, early_pay_discount: 0 }))
      expect(payment_discount_reduction_rows(no_discount, assignments, invoices, -139)).toBeNull()
    })
  })

  describe("credit-memo mode discount reductions", () => {
    // Super Coffee's Walmart check 4544294: INV33791 settled $7.03 past its NetSuite total, the
    // early pay on the memo, so the preview sends no `early_pay_discount`.
    it("reads the remittance discount from the settle amount and books negative memo lines", () => {
      const line = { deduction_id: "d91", settle_amount: 2901.1, deduction_amount: 2842.49 }
      expect(remittance_early_pay_discount(line)).toBe(58.61)
      expect(remittance_early_pay_discount({ deduction_amount: 10 })).toBe(0)
      const invoices = new Map([["i91", { invoice_number: "INV33791", total_amount: 2894.07 }]])
      const rows = payment_discount_reduction_rows(
        [{ ...line, early_pay_discount: remittance_early_pay_discount(line) }],
        { d91: "i91" },
        invoices,
        -703
      )
      expect(rows).toEqual([{ deduction_id: "d91", invoice_number: "INV33791", reduce_cents: 703 }])
      const account = { account_number: "4701", expense_account: "Early Payment Discount", product_service: null }
      expect(build_discount_reduction_lines(rows!, account)).toEqual([
        {
          gl_account_id: "4701",
          gl_account_name: "Early Payment Discount",
          amount: -7.03,
          description: "Early-pay discount not taken - INV33791",
          product_service: undefined,
        },
      ])
    })
  })

  describe("additional_discount_rows", () => {
    const invoices = new Map<string, { total_amount: number }>([
      ["i1", { total_amount: 1447.03 }],
      ["i2", { total_amount: 482.35 }],
    ])
    const assignments = { d1: "i1", d2: "i2" }
    const row = (deduction_id: string, invoice_number: string, short_cents: number, partial_cents = 0): SettlementBreakdownRow => ({
      deduction_id,
      invoice_number,
      settled_cents: 0,
      partial_cents,
      partial_check_numbers: [],
      short_cents,
      over_cents: 0,
      credited_cents: 0,
    })

    it("offers the Walmart gaps as additional discounts", () => {
      const rows = [row("d1", "INV33152", 625), row("d2", "INV33357", 1349)]
      expect(additional_discount_rows(rows, invoices, assignments)).toEqual(rows)
    })

    it("refuses partial payments, gaps beyond the band, and nothing to offer", () => {
      expect(additional_discount_rows(null, invoices, assignments)).toBeNull()
      expect(additional_discount_rows([], invoices, assignments)).toBeNull()
      expect(additional_discount_rows([row("d1", "INV33152", 625, 100)], invoices, assignments)).toBeNull()
      // 5% of 482.35 is 24.11; a $30 gap is a short pay.
      expect(additional_discount_rows([row("d2", "INV33357", 3000)], invoices, assignments)).toBeNull()
      expect(additional_discount_rows([row("dX", "INV1", 100)], invoices, assignments)).toBeNull()
    })

    it("offers the gap as a short pay only where the discount band refuses it", () => {
      // Check 4345810: Walmart discounts the whole $422.00 PO but pays for what it received, so all
      // three invoices are short well past 5% — no discount to book, flag it instead.
      const wide = [row("d1", "INV33021", 2341), row("d2", "INV33022", 544)]
      const wide_invoices = new Map<string, { total_amount: number }>([
        ["i1", { total_amount: 416.57 }],
        ["i2", { total_amount: 43.62 }],
      ])
      expect(additional_discount_rows(wide, wide_invoices, assignments)).toBeNull()
      expect(short_pay_offer_rows(wide, null, "NetSuite")).toBe(wide)
      expect(short_pay_offer_rows(wide, null, "Xero")).toBe(wide)
      // Within the band the discount wins — it closes each invoice in full, so no second offer.
      const narrow = [row("d1", "INV33152", 625)]
      expect(short_pay_offer_rows(narrow, narrow, "NetSuite")).toBeNull()
      // QuickBooks cannot settle an invoice partially at all; Sage Intacct can.
      expect(short_pay_offer_rows(wide, null, "QuickBooks")).toBeNull()
      expect(short_pay_offer_rows(wide, null, "Sage Intacct")).toBe(wide)
      expect(supports_partial_settlement("NetSuite")).toBe(true)
      expect(supports_partial_settlement("Sage Intacct")).toBe(true)
      expect(supports_partial_settlement("QuickBooks")).toBe(false)
    })

    it("spends an over-settled invoice against the shorts and says so", () => {
      // Check 4357724: INV33182 settles $1.17 past its total (Walmart's reason 100 write-off pairs
      // with it), INV33268 and INV33295 are short $28.29 and $11.04.
      const rows: SettlementBreakdownRow[] = [
        { ...row("d1", "INV33182", 0), over_cents: 117 },
        row("d2", "INV33268", 2829),
        row("d3", "INV33295", 1104),
      ]
      absorb_over_settlements(rows)

      // Proportional, odd cent to the largest short — identical to the server's split.
      expect(rows.map((r) => r.short_cents)).toEqual([0, 2829 - 85, 1104 - 32])
      expect(rows.reduce((sum, r) => sum + r.short_cents, 0)).toBe(3933 - 117)
      // The over-settlement stays on the row so the page can explain where the money went.
      expect(rows[0].over_cents).toBe(117)
      expect(build_over_settlement_note(rows)).toBe(
        "This check settles one invoice past its total — INV33182 by $1.17. That $1.17 is applied to the other invoices " +
          "on the check rather than left as unapplied cash, so any short pay shown below is net of it."
      )
      expect(build_over_settlement_note([row("d2", "INV33268", 2829)])).toBeNull()
    })

    it("still offers the additional discount when an invoice was settled over", () => {
      // The over-settled invoice needs no discount line, and its presence must not refuse the offer
      // for the invoices that are genuinely short within the band.
      const over: SettlementBreakdownRow = { ...row("d1", "INV33152", 0), over_cents: 117 }
      const short = row("d2", "INV33357", 1349)
      expect(additional_discount_rows([over, short], invoices, assignments)).toEqual([short])
      // Nothing short at all (every short absorbed) is nothing to offer.
      expect(additional_discount_rows([over], invoices, assignments)).toBeNull()
    })

    it("builds one memo line per invoice on the chosen account", () => {
      const lines = build_additional_discount_lines([row("d1", "INV33152", 625), row("d2", "INV33357", 1349)], {
        account_number: "4701",
        expense_account: "Gross Revenue : Refunds & Allowances : Early Payment Discount",
        product_service: "Discount - Early Payment",
      })
      expect(lines).toEqual([
        {
          gl_account_id: "4701",
          gl_account_name: "Gross Revenue : Refunds & Allowances : Early Payment Discount",
          amount: 6.25,
          description: "Additional early-pay discount - INV33152",
          product_service: "Discount - Early Payment",
        },
        {
          gl_account_id: "4701",
          gl_account_name: "Gross Revenue : Refunds & Allowances : Early Payment Discount",
          amount: 13.49,
          description: "Additional early-pay discount - INV33357",
          product_service: "Discount - Early Payment",
        },
      ])
    })
  })

  describe("picker_invoice_list", () => {
    const ctx: InvoiceRecommendationContext = { invoice_number: "33259", amount_dollars: 407.37 }
    const invoices = [
      { id: "a", invoice_number: "INV10001", total_amount: 50, invoice_date: "2026-08-01" },
      { id: "b", invoice_number: "INV33259", total_amount: 416.57, invoice_date: "2026-08-28" },
      { id: "c", invoice_number: "INV20002", total_amount: 407.37, invoice_date: "2026-08-02" },
      { id: "d", invoice_number: "INV30003", total_amount: 99, invoice_date: "2026-07-01" },
    ] as AccountingInvoiceJson[]

    it("orders exactly like the comparator the picker used to sort with", () => {
      const expected = [...invoices].sort((x, y) => compare_accounting_invoices_by_recommendation(x, y, ctx)).map((i) => i.id)
      expect(picker_invoice_list(invoices, "", ctx, null, 10).visible.map((i) => i.id)).toEqual(expected)
      expect(expected[0]).toBe("b")
    })

    it("caps the rows but keeps the current selection visible", () => {
      const list = picker_invoice_list(invoices, "", ctx, "d", 2)
      expect(list.visible.map((i) => i.id)).toEqual(["d", "b", "c"])
      expect(list.matched_count).toBe(4)
    })

    it("filters by the search before capping", () => {
      const list = picker_invoice_list(invoices, "2000", ctx, null, 10)
      expect(list.visible.map((i) => i.id)).toEqual(["c"])
      expect(list.matched_count).toBe(1)
    })

    it("scores each invoice once, not once per comparison", () => {
      const many = Array.from({ length: 2000 }, (_, i) => ({
        id: `i${i}`,
        invoice_number: `INV${40000 + i}`,
        total_amount: i,
        invoice_date: "2026-08-01",
      })) as AccountingInvoiceJson[]
      let reads = 0
      const counted = many.map((inv) => {
        const copy = { ...inv }
        Object.defineProperty(copy, "invoice_number", {
          get() {
            reads += 1
            return inv.invoice_number
          },
        })
        return copy
      })
      picker_invoice_list(counted, "", ctx, null, 200)
      // One scoring pass reads each number a handful of times; a comparator sort reads it ~2·log2(n) times more.
      expect(reads).toBeLessThan(many.length * 12)
    })
  })

  describe("sort_invoices_by_recommendation", () => {
    it("orders by score descending", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "INV1", amount_dollars: 10 }
      const sorted = sort_invoices_by_recommendation(
        [
          { id: "z", invoice_number: "OTHER", total_amount: 10 },
          { id: "y", invoice_number: "INV1", total_amount: 999 },
        ],
        ctx
      )
      expect(sorted[0].invoice_number).toBe("INV1")
    })

    // The sort is decorate-sort-undecorate now, so the whole tie-break chain has to be reproduced
    // there rather than delegated to the pairwise comparator.
    it("falls back to date, then number, then id when scores tie", () => {
      const ctx: InvoiceRecommendationContext = { invoice_number: "NOMATCH", amount_dollars: 0 }
      const sorted = sort_invoices_by_recommendation(
        [
          { id: "c", invoice_number: "B", total_amount: 0, invoice_date: "2026-01-01" },
          { id: "a", invoice_number: "B", total_amount: 0, invoice_date: "2026-01-01" },
          { id: "b", invoice_number: "A", total_amount: 0, invoice_date: "2026-01-01" },
          { id: "d", invoice_number: "A", total_amount: 0, invoice_date: "2026-06-01" },
        ],
        ctx
      )
      expect(sorted.map((i) => i.id)).toEqual(["d", "b", "a", "c"])
    })
  })

  describe("invoice_date_ms", () => {
    it("parses, memoizes, and falls back to 0", () => {
      const first = invoice_date_ms("2026-01-02")
      expect(first).toBe(invoice_date_ms("2026-01-02"))
      expect(first).toBeGreaterThan(0)
      expect(invoice_date_ms(null)).toBe(0)
      expect(invoice_date_ms(undefined)).toBe(0)
      expect(invoice_date_ms("not-a-date")).toBe(0)
    })
  })

  describe("best_invoice_by_recommendation", () => {
    const cases: { name: string; ctx: InvoiceRecommendationContext; invoices: AccountingInvoiceJson[] }[] = [
      {
        name: "exact invoice number wins",
        ctx: { invoice_number: "INV-7", amount_dollars: 100 },
        invoices: [
          { id: "i1", invoice_number: "INV-70", total_amount: 100 },
          { id: "i2", invoice_number: "INV-7", total_amount: 999 },
          { id: "i3", invoice_number: "OTHER", total_amount: 100 },
        ],
      },
      {
        name: "ties resolve down the date/number/id chain",
        ctx: { invoice_number: "NOMATCH", amount_dollars: 0 },
        invoices: [
          { id: "c", invoice_number: "B", total_amount: 0, invoice_date: "2026-01-01" },
          { id: "a", invoice_number: "B", total_amount: 0, invoice_date: "2026-01-01" },
          { id: "b", invoice_number: "A", total_amount: 0, invoice_date: "2026-01-01" },
        ],
      },
      {
        name: "closest amount wins when numbers are equally unrelated",
        ctx: { invoice_number: "ZZZ", amount_dollars: 50 },
        invoices: [
          { id: "i1", invoice_number: "QQQ", total_amount: 5000 },
          { id: "i2", invoice_number: "RRR", total_amount: 50 },
        ],
      },
    ]

    // The one-pass max must pick exactly what a full sort would put first — that equivalence is the
    // only reason it is safe to skip the sort on the apply page.
    for (const { name, ctx, invoices } of cases) {
      it(`${name} (matches sort_invoices_by_recommendation[0])`, () => {
        expect(best_invoice_by_recommendation(invoices, ctx)?.id).toBe(
          sort_invoices_by_recommendation(invoices, ctx)[0].id
        )
      })
    }

    it("returns undefined for an empty list", () => {
      expect(best_invoice_by_recommendation([], { invoice_number: "X", amount_dollars: 1 })).toBeUndefined()
    })
  })

  describe("seed_auto_assignments_for_apply_page", () => {
    it("assigns unique high-confidence matches", () => {
      const lines = [
        { deduction_id: "d1", invoice_number: "INV-A", deduction_amount: 10 },
        { deduction_id: "d2", invoice_number: "INV-B", deduction_amount: 20 },
      ]
      const invoices: AccountingInvoiceJson[] = [
        { id: "i1", invoice_number: "INV-A", total_amount: 10 },
        { id: "i2", invoice_number: "INV-B", total_amount: 20 },
      ]
      const out = seed_auto_assignments_for_apply_page(lines, invoices, {})
      expect(out.d1).toBe("i1")
      expect(out.d2).toBe("i2")
    })

    // Compostic's UNFI check 382228: SI00004292 and SI00004292A together pay Xero SI-00004292.
    it("gives a second line of the check an invoice the first settles only in part", () => {
      const lines = [
        { deduction_id: "d1", invoice_number: "SI00004292", deduction_amount: 2372.93, settle_amount: 2421.36 },
        { deduction_id: "d2", invoice_number: "SI00004292A", deduction_amount: 2574.73, settle_amount: 2627.28 },
      ]
      const invoices: AccountingInvoiceJson[] = [{ id: "x4292", invoice_number: "SI-00004292", total_amount: 5048.64 }]
      expect(seed_auto_assignments_for_apply_page(lines, invoices, {})).toEqual({ d1: "x4292", d2: "x4292" })
      // Already picked for the first line by a stored link: same result.
      expect(seed_auto_assignments_for_apply_page(lines, invoices, { d1: "x4292" })).toEqual({ d1: "x4292", d2: "x4292" })
    })

    it("never shares an invoice past its total, or one linked to another check", () => {
      const lines = [
        { deduction_id: "d1", invoice_number: "SI00004292", deduction_amount: 5048.64, settle_amount: 5048.64 },
        { deduction_id: "d2", invoice_number: "SI00004292A", deduction_amount: 2574.73, settle_amount: 2627.28 },
      ]
      const invoices: AccountingInvoiceJson[] = [{ id: "x4292", invoice_number: "SI-00004292", total_amount: 5048.64 }]
      expect(seed_auto_assignments_for_apply_page(lines, invoices, {}).d2).toBeUndefined()
      const elsewhere: AccountingInvoiceJson[] = [{ ...invoices[0], assigned_id: "on-other-check" }]
      expect(seed_auto_assignments_for_apply_page(lines.slice(1), elsewhere, {}).d2).toBeUndefined()
    })

    // The used-set is now extended in place instead of recomputed per line; a pick still has to be
    // unavailable to every later line.
    it("does not hand the same invoice to two lines", () => {
      const lines = [
        { deduction_id: "d1", invoice_number: "INV-A", deduction_amount: 10 },
        { deduction_id: "d2", invoice_number: "INV-A", deduction_amount: 10 },
      ]
      const invoices: AccountingInvoiceJson[] = [
        { id: "i1", invoice_number: "INV-A", total_amount: 10 },
        { id: "i2", invoice_number: "INV-A", total_amount: 10 },
      ]
      const out = seed_auto_assignments_for_apply_page(lines, invoices, {})
      expect(out.d1).not.toBe(out.d2)
      expect(new Set([out.d1, out.d2])).toEqual(new Set(["i1", "i2"]))
    })

    it("leaves invoices linked to deductions off this check alone", () => {
      const lines = [{ deduction_id: "d1", invoice_number: "INV-A", deduction_amount: 10 }]
      const invoices: AccountingInvoiceJson[] = [
        { id: "i1", invoice_number: "INV-A", total_amount: 10, assigned_id: "other-deduction" },
      ]
      expect(seed_auto_assignments_for_apply_page(lines, invoices, {}).d1).toBeUndefined()
    })
  })

  // Compostic's KeHE check 1391147 paid `SI3876078` — KeHE's PO `03876078` keyed as the invoice
  // number. The Xero invoice is `SI-00004155` for $2,893.44 with reference `3876078`; the check
  // settles $2,835.57 of it (cash $2,778.86 + $56.71 terms), the 2% gap being KeHE's BI allowance.
  describe("KeHE PO placeholder invoice numbers", () => {
    const line = {
      deduction_id: "d1",
      invoice_number: "SI3876078",
      po_number: "03876078",
      deduction_amount: 2778.86,
      settle_amount: 2835.57,
    }
    const real: AccountingInvoiceJson = { id: "x4155", invoice_number: "SI-00004155", total_amount: 2893.44, reference: "3876078" }
    const neighbour: AccountingInvoiceJson = { id: "x4150", invoice_number: "SI-00004150", total_amount: 2017.44, reference: "3871093" }

    it("recognises a PO keyed as the invoice number", () => {
      expect(invoice_number_is_po_placeholder("SI3876078", "03876078")).toBe(true)
      expect(invoice_number_is_po_placeholder("SI00004150", "03871093")).toBe(false)
      expect(invoice_number_is_po_placeholder("SI3876078", null)).toBe(false)
    })

    it("links the invoice whose reference is the line's PO", () => {
      expect(seed_auto_assignments_for_apply_page([line], [neighbour, real], {}).d1).toBe("x4155")
    })

    it("recommends the referenced invoice in the picker", () => {
      const ctx = { invoice_number: line.invoice_number, amount_dollars: line.deduction_amount, po_number: line.po_number }
      expect(score_accounting_invoice_match(ctx, real)).toBeGreaterThanOrEqual(RECOMMENDED_INVOICE_BADGE_MIN_SCORE)
      expect(score_accounting_invoice_match(ctx, neighbour)).toBeLessThan(RECOMMENDED_INVOICE_BADGE_MIN_SCORE)
    })

    it("never matches on the placeholder number itself", () => {
      const lookalike: AccountingInvoiceJson = { id: "bad", invoice_number: "SI-3876078", total_amount: 2835.57 }
      expect(seed_auto_assignments_for_apply_page([line], [lookalike], {}).d1).toBeUndefined()
    })

    it("links nothing when two invoices carry the PO or the amount disagrees", () => {
      const twin: AccountingInvoiceJson = { ...real, id: "x9999", invoice_number: "SI-00009999" }
      expect(seed_auto_assignments_for_apply_page([line], [real, twin], {}).d1).toBeUndefined()
      const far: AccountingInvoiceJson = { ...real, total_amount: 5000 }
      expect(seed_auto_assignments_for_apply_page([line], [far], {}).d1).toBeUndefined()
    })

    it("still prefers an invoice-number match over a PO match", () => {
      const normal = { deduction_id: "d2", invoice_number: "SI00004150", po_number: "03871093", deduction_amount: 1937.55, settle_amount: 1977.09 }
      expect(seed_auto_assignments_for_apply_page([normal], [real, neighbour], {}).d2).toBe("x4150")
    })

    it("explains an unassigned placeholder line", () => {
      const with_candidate = describe_unassigned_invoice_line(line, build_invoice_lookup([real]), "Xero")
      expect(with_candidate).toContain("PO 03876078")
      expect(with_candidate).toContain("SI-00004155")
      const without = describe_unassigned_invoice_line(line, build_invoice_lookup([neighbour]), "Xero")
      expect(without).toContain("No synced Xero invoice references PO 3876078")
    })

    it("explains a line whose number names no synced invoice, and stays quiet when one exists", () => {
      const missing = { invoice_number: "SI00004999", po_number: "03999999" }
      expect(describe_unassigned_invoice_line(missing, build_invoice_lookup([real]), "Xero")).toContain(
        "No synced Xero invoice is numbered SI00004999"
      )
      const present = { invoice_number: "SI00004150", po_number: null }
      expect(describe_unassigned_invoice_line(present, build_invoice_lookup([neighbour]), "Xero")).toBeNull()
    })

    it("builds the apply error from the hints", () => {
      expect(build_unassigned_invoices_error([{ invoice_number: "A", hint: null }])).toBe("Assign every invoice before applying.")
      expect(build_unassigned_invoices_error([{ invoice_number: "SI3876078", hint: "Why." }])).toBe(
        "Assign every invoice before applying. SI3876078: Why."
      )
    })
  })

  // Compostic's KeHE check 1391147 once linked: SI-00004155 is $2,893.44, the check settles $2,835.57.
  describe("plan_bi_allowance_rows", () => {
    const bi = { gl_account_id: "5069", gl_account_name: "BDF (Business Development Funds)", percentage: 2 }
    const invoices = new Map([["x4155", { total_amount: 2893.44 }]])
    const assignments = { d1: "x4155" }
    const row = (short_cents: number): SettlementBreakdownRow => ({
      deduction_id: "d1",
      invoice_number: "SI-00004155",
      settled_cents: 289_344 - short_cents,
      partial_cents: 0,
      partial_check_numbers: [],
      short_cents,
      over_cents: 0,
      credited_cents: 0,
    })

    it("writes the whole BI-sized short off", () => {
      const plan = plan_bi_allowance_rows([row(5787)], invoices, assignments, 2)
      expect(plan.rows).toEqual([{ deduction_id: "d1", invoice_number: "SI-00004155", booked_cents: 5787 }])
      expect(plan.remaining?.[0].short_cents).toBe(0)
      const [line] = build_bi_allowance_lines(plan.rows, bi)
      expect(line).toMatchObject({ gl_account_id: "5069", amount: 57.87, description: "KeHE BI allowance - SI-00004155" })
      expect(build_bi_allowance_note(plan.rows, bi)).toContain("$57.87")
    })

    // Cadence's Walmart check 4452784: Xero's INV-3314 is $357.60, Walmart pays $348.66 and lists
    // no discount. The $8.94 is its 2.5% allowance, booked under Walmart's name, not KeHE's.
    it("books another payer's allowance under that payer's label", () => {
      const walmart = { gl_account_id: "4410", gl_account_name: "Walmart allowances", percentage: 2.5, label: "Walmart allowance" }
      const short: SettlementBreakdownRow = { ...row(894), invoice_number: "INV-3314", settled_cents: 34_866 }
      const plan = plan_bi_allowance_rows([short], new Map([["x3314", { total_amount: 357.6 }]]), { d1: "x3314" }, 2.5)
      expect(plan.remaining?.[0].short_cents).toBe(0)
      const [line] = build_bi_allowance_lines(plan.rows, walmart)
      expect(line).toMatchObject({ gl_account_id: "4410", amount: 8.94, description: "Walmart allowance - INV-3314" })
      const note = build_bi_allowance_note(plan.rows, walmart)
      expect(note).toContain("Walmart allowance: 2.5% of each invoice")
      expect(note).not.toContain("KeHE")
    })

    it("leaves the rest of a wider short as a short pay, and a narrower one alone", () => {
      expect(plan_bi_allowance_rows([row(8787)], invoices, assignments, 2).remaining?.[0].short_cents).toBe(3000)
      const narrow = plan_bi_allowance_rows([row(3000)], invoices, assignments, 2)
      expect(narrow.rows).toEqual([])
      expect(narrow.remaining?.[0].short_cents).toBe(3000)
      expect(plan_bi_allowance_rows(null, invoices, assignments, 2)).toEqual({ rows: [], remaining: null })
    })

    it("matches the server's rounding", () => {
      expect(bi_allowance_cents(289_344, 2)).toBe(5787)
      expect(bi_allowance_booking_cents(5788, 5787)).toBe(5788)
    })
  })

  describe("used_accounting_invoice_ids_for_apply_page", () => {
    it("marks invoices linked off-check as used", () => {
      const check_ids = new Set(["d1"])
      const invoices: AccountingInvoiceJson[] = [
        { id: "x", invoice_number: "Z", total_amount: 1, assigned_id: "other-deduction" },
      ]
      const used = used_accounting_invoice_ids_for_apply_page(check_ids, {}, invoices)
      expect(used.has("x")).toBe(true)
    })
  })

  describe("collect_splits_for_gl_line", () => {
    it("collects only coded splits matching gl", () => {
      const transactions: TransactionJson[] = [
        {
          id: "d1",
          invoice_number: "R1",
          kind: "deduction",
          accounting_status: "coded",
          coded: true,
          can_auto_code: false,
          splits: [
            { id: "s1", deduction_id: "d1", name: "n", amount: 10, account_id: "a1", account_number: "48300" },
          ],
        } as unknown as TransactionJson,
      ]
      const rows = collect_splits_for_gl_line(transactions, "48300")
      expect(rows).toHaveLength(1)
      expect(rows[0].invoice_ref).toBe("R1")
    })

    it("shows positive-amount transactions (repayments, reversals) as a negative contribution", () => {
      const transactions: TransactionJson[] = [
        {
          id: "rp",
          invoice_number: "RP3902",
          kind: "repayment",
          invoice_amount: 1094.65,
          accounting_status: "coded",
          coded: true,
          can_auto_code: false,
          splits: [
            { id: "s1", deduction_id: "rp", name: "n", amount: 1094.65, account_id: "a1", account_number: "1411" },
          ],
        } as unknown as TransactionJson,
        {
          id: "rev",
          invoice_number: "BTTAXMAR26",
          kind: "deduction",
          invoice_amount: 67.2,
          accounting_status: "coded",
          coded: true,
          can_auto_code: false,
          splits: [{ id: "s2", deduction_id: "rev", name: "n", amount: 67.2, account_id: "a2", account_number: "1411" }],
        } as unknown as TransactionJson,
      ]
      const rows = collect_splits_for_gl_line(transactions, "1411")
      expect(rows).toHaveLength(2)
      expect(rows[0].amount).toBe(-1094.65)
      expect(rows[1].amount).toBe(-67.2)
    })
  })

  describe("credit_memo_split_amount", () => {
    it("negates positive-amount transactions regardless of stored split sign", () => {
      expect(credit_memo_split_amount(1094.65, 1094.65)).toBe(-1094.65)
      expect(credit_memo_split_amount(1094.65, -1094.65)).toBe(-1094.65)
      expect(credit_memo_split_amount(67.2, 67.2)).toBe(-67.2)
    })

    it("keeps normal (negative-amount) deductions positive", () => {
      expect(credit_memo_split_amount(-500, -500)).toBe(500)
      expect(credit_memo_split_amount(-500, 500)).toBe(500)
    })
  })

  describe("prepayment notes and errors", () => {
    const advance: PrepaymentRow = {
      invoice_number: "SI00003930PP",
      invoice_stem: "SI00003930",
      role: "advance",
      amount: "11001.66",
    }
    const resolved_reversal: PrepaymentRow = {
      invoice_number: "SI00003888PP",
      invoice_stem: "SI00003888",
      role: "reversal",
      amount: 6727.13,
      settled_invoice_id: "ded-1",
    }
    const unresolved_reversal: PrepaymentRow = {
      invoice_number: "SI00000191PP",
      invoice_stem: "SI00000191",
      role: "reversal",
      amount: "11258.40",
    }
    const pair_a: PrepaymentRow = { invoice_number: "SI00003352PP", invoice_stem: "SI00003352", role: "self_cancelling", amount: 7759.07 }
    const pair_b: PrepaymentRow = { invoice_number: "SI00003352PP", invoice_stem: "SI00003352", role: "self_cancelling", amount: 7759.07 }

    it("returns no blocking error when every reversal is resolved", () => {
      expect(build_unresolved_prepayments_error([advance, resolved_reversal, pair_a, pair_b])).toBe("")
    })

    it("blocks on a reversal with no matching invoice line, naming it with its amount", () => {
      const message = build_unresolved_prepayments_error([unresolved_reversal, advance])
      expect(message).toContain("SI00000191PP")
      expect(message).toContain("$11,258.40")
      expect(message).toContain("Check the remittance")
      // The advance is not part of the blocking message.
      expect(message).not.toContain("SI00003930PP")
    })

    it("explains an advance as the early payment of its invoice, decimal-string amounts included", () => {
      const notes = build_prepayment_notes([advance])
      expect(notes).toHaveLength(1)
      expect(notes[0]).toContain("paid invoice SI00003930 early")
      expect(notes[0]).toContain("$11,001.66")
      expect(notes[0]).toContain("assign the matching SI00003930 invoice")
    })

    it("explains a partial reversal as a deduction to code, and never blocks on it", () => {
      const partial: PrepaymentRow = {
        invoice_number: "SI00004019PP",
        invoice_stem: "SI00004019",
        role: "partial_reversal",
        amount: "10282.00",
        settled_invoice_id: "ded-2",
        clawback: "2372.93",
      }
      expect(build_unresolved_prepayments_error([partial])).toBe("")
      const notes = build_prepayment_notes([partial])
      expect(notes).toHaveLength(1)
      expect(notes[0]).toContain("$10,282.00")
      expect(notes[0]).toContain("$2,372.93")
      expect(notes[0]).toContain("deduction against SI00004019")
    })

    // Compostic's 360143: UNFI took the 5,214.74 advance for SI00004145 back and never listed the
    // invoice. The card explains the whole-advance clawback and never blocks on it.
    it("explains a reversal with no invoice line as the whole advance taken back, never blocking", () => {
      const full: PrepaymentRow = {
        invoice_number: "SI00004145PP",
        invoice_stem: "SI00004145",
        role: "partial_reversal",
        amount: "5214.74",
        clawback: "5214.74",
      }
      expect(build_unresolved_prepayments_error([full])).toBe("")
      const summary = build_prepayment_summary([full])
      expect(summary).toHaveLength(1)
      expect(summary[0].tone).toBe("info")
      expect(summary[0].label).toBe("Taken back in full")
      expect(summary[0].amount).toBe(-5214.74)
      expect(summary[0].paired_line).toBeUndefined()
      expect(summary[0].note).toContain("without listing the invoice again")
      expect(summary[0].note).toContain("$5,214.74")
      expect(summary[0].note).toContain("deduction against SI00004145")
    })

    it("explains a resolved reversal and collapses a self-cancelling pair to one note", () => {
      const notes = build_prepayment_notes([resolved_reversal, pair_a, pair_b, unresolved_reversal])
      expect(notes).toHaveLength(2)
      expect(notes[0]).toContain("taking back the advance")
      expect(notes[0]).toContain("SI00003888")
      expect(notes[1]).toContain("cancel out")
      // Unresolved reversals belong to the blocking message, not the notes.
      expect(notes.join(" ")).not.toContain("SI00000191PP")
    })

    // Kitu's 373145: the advance was applied on 372684, and this check lists INV33141 again as a
    // portal line (`INV33141-0-East`) and takes the advance back. The card must name the pair that
    // nets out and carry no blocking row.
    it("summarises a resolved reversal as a pair that nets to zero, naming the remittance line it cancels", () => {
      const kitu: PrepaymentRow = {
        deduction_id: "pp-1",
        invoice_number: "INV33141PP-1-East",
        invoice_stem: "INV33141",
        role: "reversal",
        amount: "14582.40",
        settled_invoice_id: "inv-1",
        settled_invoice_number: "INV33141-0-East",
      }
      const summary = build_prepayment_summary([kitu])
      expect(summary).toHaveLength(1)
      expect(summary[0].tone).toBe("info")
      expect(summary[0].label).toBe("Nets to zero")
      expect(summary[0].amount).toBe(-14582.4)
      expect(summary[0].paired_line).toEqual({ invoice_number: "INV33141-0-East", amount: 14582.4 })
      expect(summary[0].note).toContain("INV33141-0-East")
      expect(summary[0].note).toContain("Nothing to code")
      expect(build_unresolved_prepayments_error([kitu])).toBe("")
    })

    it("summarises an unresolved reversal as a blocked row carrying the blocking message", () => {
      const summary = build_prepayment_summary([unresolved_reversal, advance])
      expect(summary.map((row: PrepaymentSummaryRow) => row.tone)).toEqual(["blocked", "info"])
      expect(summary[0].note).toBe(build_unresolved_prepayments_error([unresolved_reversal]))
      expect(summary[0].amount).toBe(-11258.4)
      // An advance is signed positive: cash arriving.
      expect(summary[1].amount).toBe(11001.66)
      expect(summary[1].label).toBe("Paid early")
    })

    it("finds the advance behind a rewritten invoice line by deduction id", () => {
      const rewritten: PrepaymentRow = { ...advance, deduction_id: "ded-adv" }
      expect(advance_behind_line([rewritten, resolved_reversal], "ded-adv")).toBe(rewritten)
      expect(advance_behind_line([rewritten, resolved_reversal], "ded-1")).toBeNull()
    })
  })

  describe("build_repayment_invoice_note", () => {
    const row: RepaymentInvoiceRow = {
      reference: "SI00003705A",
      total: "2414.16",
      credit_total: "907.86",
      supported: true,
      accounting_system: "Xero",
      customer_name: "Kehe Distributors LLC",
      lines: [{ gl_account_id: "5037", gl_account_name: "Returns and Inventory Adjustments", amount: "2414.16" }],
    }

    it("says what the system will do with a KeHE repayment check", () => {
      expect(build_repayment_invoice_note(row)).toBe(
        "This check includes $2,414.16 that Kehe Distributors LLC is paying back (more than the $907.86 deducted on it). " +
          "A payment in Xero has to be applied to an invoice, so when you click Apply, MarginWiz will:"
      )
      expect(build_repayment_invoice_steps(row)).toEqual([
        "Create a Xero invoice to Kehe Distributors LLC for $2,414.16, posted to 5037 Returns and Inventory Adjustments.",
        "Apply the $907.86 of deductions on this check against that invoice.",
        "Apply the remaining $1,506.30 of the check to it, along with the other invoices this check pays.",
      ])
    })

    it("tells a provider with no invoice path to record it by hand, and is null when not a repayment check", () => {
      const unsupported = { ...row, supported: false, accounting_system: "Sage Intacct" }
      const note = build_repayment_invoice_note(unsupported) ?? ""
      expect(note).toContain("there is no invoice for this money")
      expect(note).toContain("record this check in Sage Intacct by hand")
      expect(build_repayment_invoice_steps(unsupported)).toEqual([])
      expect(build_repayment_invoice_note(null)).toBeNull()
      expect(build_repayment_invoice_steps(null)).toEqual([])
    })

    // Super Coffee's Walmart check 4570849: a $32,929.38 repayment of 825284474 parked in open
    // deductions, beside $257.63 of deductions. NetSuite gets the invoice; nobody rebills by hand.
    it("has NetSuite invoice the repayment itself, with no step about deductions when there are none", () => {
      const walmart: RepaymentInvoiceRow = {
        reference: "Repayment on check 4570849",
        total: "32929.38",
        credit_total: "257.63",
        supported: true,
        accounting_system: "NetSuite",
        customer_name: "568 Walmart",
        lines: [{ gl_account_id: "1290", gl_account_name: "Open Deductions", amount: "32929.38" }],
      }
      const steps = build_repayment_invoice_steps(walmart)
      expect(steps[0]).toBe("Create a NetSuite invoice to 568 Walmart for $32,929.38, posted to 1290 Open Deductions.")
      expect(steps[2]).toContain("$32,671.75")
      const none_deducted = { ...walmart, credit_total: "0" }
      expect(build_repayment_invoice_steps(none_deducted)).toHaveLength(2)
      expect(build_repayment_invoice_note(none_deducted)).toBe(
        "This check includes $32,929.38 that 568 Walmart is paying back. A payment in NetSuite has to be applied to an invoice, so when you click Apply, MarginWiz will:"
      )
    })
  })

  describe("rebill settlements", () => {
    // Super Coffee's Grocery Supply check 98933: the repayment of the INV33661 short pay (taken on
    // 92917), rebilled as INV33894.
    const settlement = {
      deduction_id: "ded-rep",
      repayment_number: "INV33661*",
      repaid_invoice_number: "INV33661",
      repaid_check_number: "92917",
      rebill_invoice_number: "INV33894",
      amount: "4330.48",
    }

    it("finds the settlement behind an invoice line, and nothing behind an ordinary one", () => {
      expect(rebill_settlement_behind_line([settlement], "ded-rep")).toBe(settlement)
      expect(rebill_settlement_behind_line([settlement], "ded-1")).toBeNull()
    })

    it("explains the line against the remittance", () => {
      expect(build_rebill_settlement_note(settlement)).toBe(
        "Remitted as INV33661*: the repayment of INV33661 (taken on check 92917), which was rebilled as INV33894. This payment settles that invoice."
      )
      expect(build_rebill_settlement_note({ ...settlement, repaid_check_number: null })).toContain("the repayment of INV33661, which was rebilled")
    })
  })

  describe("build_missing_chargeback_type_note", () => {
    const lines: ProductGroupLineRow[] = [
      { gl_account_id: "48003", amount: 70, chargeback_type: "Spoilage" },
      { gl_account_id: "48003", amount: "20.10", chargeback_type: null },
      { gl_account_id: "48005", amount: 5.25, chargeback_type: " " },
    ]
    it("is empty when the org carries no chargeback type or every line has one", () => {
      expect(build_missing_chargeback_type_note(lines, null)).toBe("")
      expect(build_missing_chargeback_type_note([lines[0]], "Chargeback Type")).toBe("")
    })
    it("totals the lines with none and names their accounts", () => {
      const note = build_missing_chargeback_type_note(lines, "Chargeback Type")
      expect(note.startsWith("2 lines ($25.35, GL 48003, 48005) have no Chargeback Type.")).toBe(true)
      expect(build_missing_chargeback_type_note([lines[1]], "Chargeback Type")).toContain("1 line ($20.10, GL 48003) has no")
    })
  })

  describe("build_missing_product_group_note", () => {
    const lines = [
      { gl_account_id: "48003", amount: 90, product_group: "Protein Sticks" },
      { gl_account_id: "48003", amount: "20.10", product_group: null },
      { gl_account_id: "48005", amount: 5.25, product_group: " " },
    ]

    it("is empty without the field, or when every line has a group", () => {
      expect(build_missing_product_group_note(lines, null)).toBe("")
      expect(build_missing_product_group_note([lines[0]], "Related Product Group")).toBe("")
    })

    it("totals the lines with no group and names their GL accounts", () => {
      const note = build_missing_product_group_note(lines, "Related Product Group")
      expect(note).toContain("2 lines ($25.35, GL 48003, 48005) have no Related Product Group")
      expect(build_missing_product_group_note([lines[1]], "Related Product Group")).toContain("1 line ($20.10, GL 48003) has no")
    })
  })

  describe("build_uncoded_transactions_error", () => {
    it("is empty when every transaction is coded", () => {
      expect(build_uncoded_transactions_error([])).toBe("")
    })

    it("names each uncoded transaction and totals the dollars missing from the credit memo", () => {
      // Compostic check 1370213: the uncoded repayment plus the invoice's uncoded early-pay discount
      // are what the balance error was reporting as an unexplained $1,083.08 gap.
      const message = build_uncoded_transactions_error([
        { invoice_number: "SI00003572A", kind: "repayment", missing_amount: 1172.59 },
        { invoice_number: "SI00003754", kind: "invoice", missing_amount: 44.3 },
      ])
      expect(message).toContain("These 2 transactions have no GL coding")
      expect(message).toContain("$1,216.89")
      expect(message).toContain("SI00003572A (repayment, $1,172.59)")
      expect(message).toContain("SI00003754 (invoice, $44.30)")
    })

    it("uses singular wording for one transaction", () => {
      const message = build_uncoded_transactions_error([{ invoice_number: "RP-1", kind: "repayment", missing_amount: 10 }])
      expect(message).toContain("This transaction has no GL coding")
    })
  })

  describe("applied_remote_link", () => {
    it("builds QuickBooks payment and credit memo links", () => {
      expect(applied_remote_link("quickbooks", "payment", "123")).toBe("https://qbo.intuit.com/app/recvpayment?txnId=123")
      expect(applied_remote_link("QuickBooks", "credit_memo", "456")).toBe("https://qbo.intuit.com/app/creditmemo?txnId=456")
    })

    it("links a Xero payment via the bank transaction view", () => {
      expect(applied_remote_link("xero", "payment", "8c313714-d0db-4d1f-89b8-db0e2c53aaa8")).toBe(
        "https://go.xero.com/Bank/ViewTransaction.aspx?bankTransactionID=8c313714-d0db-4d1f-89b8-db0e2c53aaa8"
      )
    })

    it("links the first payment when a Xero apply produced one payment per invoice", () => {
      expect(applied_remote_link("xero", "payment", "aaa, bbb")).toBe(
        "https://go.xero.com/Bank/ViewTransaction.aspx?bankTransactionID=aaa"
      )
    })

    it("links the credit note when the check was fully settled by credit notes", () => {
      expect(applied_remote_link("xero", "payment", "creditnote:ccc")).toBe(
        "https://go.xero.com/AccountsReceivable/ViewCreditNote.aspx?creditNoteID=ccc"
      )
      expect(applied_remote_link("xero", "credit_memo", "ddd")).toBe(
        "https://go.xero.com/AccountsReceivable/ViewCreditNote.aspx?creditNoteID=ddd"
      )
    })

    it("returns null for providers without public URLs and empty ids", () => {
      expect(applied_remote_link("sageintacct", "payment", "x")).toBeNull()
      expect(applied_remote_link("netsuite", "payment", "x")).toBeNull()
      expect(applied_remote_link(null, "payment", "x")).toBeNull()
      expect(applied_remote_link("xero", "payment", "  ")).toBeNull()
      expect(applied_remote_link("xero", "payment", "creditnote:")).toBeNull()
    })
  })

  describe("compute_short_pay_breakdown", () => {
    // The real Compostic check 1368083: Xero totals 2252.52 / 3982.50 vs settle 2207.47 / 3902.85.
    const lines = [
      { deduction_id: "d1", settle_amount: 2207.47 },
      { deduction_id: "d2", settle_amount: 3902.85 },
    ]
    const assignments = { d1: "i1", d2: "i2" }
    const invoices = new Map([
      ["i1", { invoice_number: "SI-00003704", total_amount: 2252.52 }],
      ["i2", { invoice_number: "SI-00003705", total_amount: 3982.5 }],
    ])

    const no_links = new Map<string, InvoiceLinkSummary>()

    it("attributes the whole delta per invoice", () => {
      const rows = compute_settlement_breakdown(lines, assignments, invoices, no_links, 12470)
      expect(rows).toEqual([
        { deduction_id: "d1", invoice_number: "SI-00003704", settled_cents: 220747, partial_cents: 0, partial_check_numbers: [], short_cents: 4505, over_cents: 0, credited_cents: 0 },
        { deduction_id: "d2", invoice_number: "SI-00003705", settled_cents: 390285, partial_cents: 0, partial_check_numbers: [], short_cents: 7965, over_cents: 0, credited_cents: 0 },
      ])
    })

    it("sums two lines of the check that pay one invoice", () => {
      // Compostic 382228: SI00004292 + SI00004292A settle Xero SI-00004292 in full; SI-00004291 is short $1.00.
      const two = [
        { deduction_id: "a", settle_amount: 2421.36 },
        { deduction_id: "b", settle_amount: 2627.28 },
        { deduction_id: "c", settle_amount: 3819.68 },
      ]
      const picks = { a: "x4292", b: "x4292", c: "x4291" }
      const invs = new Map([
        ["x4292", { invoice_number: "SI-00004292", total_amount: 5048.64 }],
        ["x4291", { invoice_number: "SI-00004291", total_amount: 3820.68 }],
      ])
      expect(compute_settlement_breakdown(two, picks, invs, no_links, 100)).toEqual([
        { deduction_id: "c", invoice_number: "SI-00004291", settled_cents: 381968, partial_cents: 0, partial_check_numbers: [], short_cents: 100, over_cents: 0, credited_cents: 0 },
      ])
      // Short together: one row for the invoice, hung off the lowest line id.
      const short = [{ deduction_id: "b", settle_amount: 2627.28 }, { deduction_id: "a", settle_amount: 2400 }]
      expect(compute_settlement_breakdown(short, { a: "x4292", b: "x4292" }, invs, no_links, 2136)).toEqual([
        { deduction_id: "a", invoice_number: "SI-00004292", settled_cents: 502728, partial_cents: 0, partial_check_numbers: [], short_cents: 2136, over_cents: 0, credited_cents: 0 },
      ])
    })

    it("returns null when the shorts don't sum to the delta", () => {
      expect(compute_settlement_breakdown(lines, assignments, invoices, no_links, 12471)).toBeNull()
    })

    it("returns null when a line has no settle amount or assignment", () => {
      expect(compute_settlement_breakdown([{ deduction_id: "d1" }], assignments, invoices, no_links, 100)).toBeNull()
      expect(compute_settlement_breakdown([{ deduction_id: "dX", settle_amount: 10 }], assignments, invoices, no_links, 100)).toBeNull()
    })

    it("returns null for over-settled invoices and non-positive deltas", () => {
      const over = [{ deduction_id: "d1", settle_amount: 2300 }]
      expect(compute_settlement_breakdown(over, assignments, invoices, no_links, 100)).toBeNull()
      expect(compute_settlement_breakdown(lines, assignments, invoices, no_links, 0)).toBeNull()
      expect(compute_settlement_breakdown(lines, assignments, invoices, no_links, -50)).toBeNull()
    })

    it("skips fully settled invoices but still requires a non-empty breakdown", () => {
      const settled = [{ deduction_id: "d1", settle_amount: 2252.52 }]
      expect(compute_settlement_breakdown(settled, { d1: "i1" }, invoices, no_links, 100)).toBeNull()
    })

    // Compostic's 2501-92645: Xero already applied the brand's CN-00000117 ($198.84) to SI-00004219,
    // Thrive lists that same credit, and the invoice line settles the full $4,579.10 face. With the
    // credit note off the memo the delta is exactly $198.84 — explained, not a short pay.
    it("carries a credit the accounting system already applied even when the line settles in full", () => {
      const line = [{ deduction_id: "d1", settle_amount: 4579.1 }]
      const inv = new Map([["i1", { invoice_number: "SI-00004219", total_amount: 4579.1 }]])
      const credited = new Map([["i1", 19884]])
      const rows = compute_settlement_breakdown(line, { d1: "i1" }, inv, no_links, 19884, credited)
      expect(rows).toEqual([
        { deduction_id: "d1", invoice_number: "SI-00004219", settled_cents: 457910, partial_cents: 0, partial_check_numbers: [], short_cents: 0, over_cents: 0, credited_cents: 19884 },
      ])
      // Nothing to offer as a short pay or a discount, nothing on another check.
      expect(short_pay_offer_rows(rows, null, "Xero")!.filter((r) => r.short_cents > 0)).toEqual([])
      expect(additional_discount_rows(rows, inv, { d1: "i1" })).toBeNull()
      expect(build_partial_payment_note(rows!, "Xero")).toBeNull()
      // The credit counts toward the delta exactly once.
      expect(compute_settlement_breakdown(line, { d1: "i1" }, inv, no_links, 19885, credited)).toBeNull()
      // A short on the same invoice and the applied credit are both kept and together are the delta.
      const short_line = [{ deduction_id: "d1", settle_amount: 4479.1 }]
      const both = compute_settlement_breakdown(short_line, { d1: "i1" }, inv, no_links, 10000 + 19884, credited)
      expect(both![0]).toMatchObject({ short_cents: 10000, credited_cents: 19884 })
    })
  })

  describe("already applied credit notes", () => {
    const rows = [
      { deduction_id: "cn", invoice_number: "CN-00000117", amount: 198.84, accounting_invoice_id: "i1", accounting_invoice_number: "SI-00004219", credit_note_number: "CN-00000117" },
    ]

    it("sums the applied cents per accounting invoice", () => {
      expect(credited_by_invoice_from_preview(rows)).toEqual(new Map([["i1", 19884]]))
      expect(credited_by_invoice_from_preview([...rows, { ...rows[0], deduction_id: "x", amount: "0.16" }])).toEqual(new Map([["i1", 19900]]))
      expect(credited_by_invoice_from_preview([])).toEqual(new Map())
    })

    it("no longer flags the invoice as over-applied once the credit is off the charge", () => {
      const selected = [{ id: "i1", invoice_number: "SI-00004219", total_amount: 4579.1, amount_due: 4380.26 }]
      expect(find_overpaid_invoices(selected).map((i) => i.invoice_number)).toEqual(["SI-00004219"])
      expect(find_overpaid_invoices(selected, credited_by_invoice_from_preview(rows))).toEqual([])
      // A balance short by more than the credit is still over-applied, by the rest.
      const shorter = [{ id: "i1", invoice_number: "SI-00004219", total_amount: 4579.1, amount_due: 4300 }]
      expect(find_overpaid_invoices(shorter, credited_by_invoice_from_preview(rows))).toEqual([
        { invoice_number: "SI-00004219", outstanding_cents: 430000, charge_cents: 438026 },
      ])
    })

    it("explains the missing remittance lines", () => {
      const note = build_already_credited_note(rows, "Xero")!
      expect(note).toContain("One line on this remittance is already applied in Xero")
      expect(note).toContain("CN-00000117 ($198.84, applied to SI-00004219 as CN-00000117)")
      expect(note).toContain("$198.84 is left off the credit memo")
      expect(build_already_credited_note([], "Xero")).toBeNull()
    })

    // Thrive 2515-93317: CN-0589 is the brand's own Xero note for August's brand funding; the
    // remittance nets $1,068.03 of it off SI-00004218 without a line of its own.
    const unlisted = [
      { deduction_id: null, invoice_number: null, amount: 1068.03, accounting_invoice_id: "i2", accounting_invoice_number: "SI-00004218", credit_note_number: "CN-0589" },
    ]

    it("counts a credit note the remittance does not list against its invoice", () => {
      expect(credited_by_invoice_from_preview(unlisted)).toEqual(new Map([["i2", 106803]]))
      const selected = [{ id: "i2", invoice_number: "SI-00004218", total_amount: 5004.74, amount_due: 3936.71 }]
      expect(find_overpaid_invoices(selected, credited_by_invoice_from_preview(unlisted))).toEqual([])
    })

    it("explains a credit note the remittance nets without listing", () => {
      const note = build_already_credited_note(unlisted, "Xero")!
      expect(note).not.toContain("line on this remittance")
      expect(note).toContain("A credit note already applied in Xero covers what the customer netted off this payment without listing it")
      expect(note).toContain("CN-0589 ($1,068.03, applied to SI-00004218)")
      expect(note).toContain("$1,068.03 is left off the credit memo")
      const both = build_already_credited_note([...rows, ...unlisted], "Xero")!
      expect(both).toContain("One line on this remittance is already applied in Xero")
      expect(both).toContain("$1,266.87 is left off the credit memo")
    })
  })

  // 1st Phorm's McLane check 3000843308 in miniature: invoice 3337736 ($2,484.00, 2% taken on the
  // payment), a $100 deduction, and three `R` lines paying invoices NetSuite already shows as paid.
  describe("unapplied cash", () => {
    const line = (id: string, number: string, cash: number, settle: number, discount?: number): InvoiceAssignmentLine =>
      ({
        deduction_id: id,
        invoice_number: number,
        deduction_amount: cash,
        kind: "invoice",
        settle_amount: settle,
        early_pay_discount: discount,
      }) as InvoiceAssignmentLine
    const preview = {
      invoice_assignments: [
        line("a", "3337736A", 2434.32, 2484, 49.68),
        line("r1", "3394290R", 28.2, 28.2),
        line("r2", "3394291R", 9.4, 9.4),
        line("r3", "3394293R", 9.4, 9.4),
      ],
      credit_memo_lines: [{ gl_account_id: "4100", gl_account_name: "Promo", amount: 100 }],
      check_amount: 2381.32,
      already_credited: [],
    } as unknown as CashApplicationPreview
    const invoices = [{ id: "inv-a", invoice_number: "3337736", total_amount: 2484, amount_due: 2484 } as AccountingInvoiceJson]

    it("charges an invoice once when two lines of the check pay it", () => {
      const split = {
        invoice_assignments: [line("a", "SI00004292", 2372.93, 2421.36), line("b", "SI00004292A", 2574.73, 2627.28)],
        credit_memo_lines: [{ gl_account_id: "4100", gl_account_name: "Discount", amount: 100.98 }],
        check_amount: 4947.66,
        already_credited: [],
      } as unknown as CashApplicationPreview
      const inv = [{ id: "x4292", invoice_number: "SI-00004292", total_amount: 5048.64, amount_due: 5048.64 } as AccountingInvoiceJson]
      const balance = compute_apply_balance(split, { a: "x4292", b: "x4292" }, inv)
      expect(balance.selected_invoice_total_cents).toBe(504864)
      expect(balance.payment_balance_delta_cents).toBe(0)
    })

    it("blocks on the R lines until they are left unapplied, then balances to the cent", () => {
      const blocked = compute_apply_balance(preview, { a: "inv-a" }, invoices)
      expect(blocked.unassigned_count).toBe(3)

      const balance = compute_apply_balance(preview, { a: "inv-a" }, invoices, new Set(["r1", "r2", "r3"]))
      expect(balance.unassigned_count).toBe(0)
      expect(balance.unapplied_cash_cents).toBe(4_700)
      expect(balance.unapplied_lines.map((l: InvoiceAssignmentLine) => l.invoice_number)).toEqual(["3394290R", "3394291R", "3394293R"])
      expect(balance.invoice_lines.map((l: InvoiceAssignmentLine) => l.invoice_number)).toEqual(["3337736A"])
      // 2,484.00 = (2,381.32 − 47.00) cash + 100.00 memo + 49.68 discount
      expect(balance.base_applied_total_cents).toBe(248_400)
      expect(balance.payment_balance_delta_cents).toBe(0)
      // The payment is still the whole check.
      expect(balance.check_cash_cents).toBe(238_132)
    })

    it("ignores an invoice still picked for a line left unapplied", () => {
      const paid = { id: "inv-r1", invoice_number: "3394290", total_amount: 651.6, amount_due: 0 } as AccountingInvoiceJson
      const balance = compute_apply_balance(preview, { a: "inv-a", r1: "inv-r1" }, [...invoices, paid], new Set(["r1", "r2", "r3"]))
      expect(balance.selected_invoice_total_cents).toBe(248_400)
      expect(balance.payment_balance_delta_cents).toBe(0)
    })

    it("offers it only where the payment can carry unapplied cash", () => {
      const r1 = preview.invoice_assignments[1]
      const discounted = preview.invoice_assignments[0]
      expect(can_leave_line_unapplied({ unapplied_cash_supported: true, early_pay_on_payment: true }, r1)).toBe(true)
      expect(can_leave_line_unapplied({ unapplied_cash_supported: false, early_pay_on_payment: true }, r1)).toBe(false)
      expect(can_leave_line_unapplied({ early_pay_on_payment: true }, r1)).toBe(false)
      // A discounted line: fine when the discount rides on the payment, refused when it is a memo line.
      expect(can_leave_line_unapplied({ unapplied_cash_supported: true, early_pay_on_payment: true }, discounted)).toBe(true)
      expect(can_leave_line_unapplied({ unapplied_cash_supported: true, early_pay_on_payment: false }, discounted)).toBe(false)
      expect(can_leave_line_unapplied({ unapplied_cash_supported: true, early_pay_on_payment: false }, r1)).toBe(true)
      expect(
        can_leave_line_unapplied({ unapplied_cash_supported: true, early_pay_on_payment: true }, { ...r1, kind: "deduction", deduction_amount: -5 })
      ).toBe(false)
    })

    it("says plainly what stays on the account", () => {
      expect(build_unapplied_cash_note([], 238_132, "NetSuite")).toBeNull()
      const note = build_unapplied_cash_note(preview.invoice_assignments.slice(1), 238_132, "NetSuite")!
      expect(note).toContain("Unapplied: $47.00 stays on the customer's account in NetSuite")
      expect(note).toContain("3394290R ($28.20), 3394291R ($9.40), 3394293R ($9.40)")
      expect(note).toContain("created for the full $2,381.32")
      expect(note).toContain("$2,334.32 of it settles the selected invoices")
    })

    it("names the unapplied cash when the sides still do not match", () => {
      const msg = build_payment_balance_error({
        selected_invoice_total_cents: 250_000,
        check_cash_cents: 238_132,
        credit_memo_total_cents: 10_000,
        unapplied_cash_cents: 4_700,
        applied_total_cents: 243_432,
        delta_cents: 250_000 - 243_432,
      })
      expect(msg).toContain("check cash ($2,381.32) less the $47.00 left unapplied plus the credit memo ($100.00) comes to $2,434.32")
    })
  })

  describe("build_payment_balance_error", () => {
    it("explains when invoices exceed cash plus credit", () => {
      const msg = build_payment_balance_error({
        selected_invoice_total_cents: 4_926_558,
        check_cash_cents: 1_063_236,
        credit_memo_total_cents: 4_238_732,
        applied_total_cents: 5_301_968,
        delta_cents: 4_926_558 - 5_301_968,
      })
      // Sides are spelled out, with the gap and its direction.
      expect(msg).toContain("$49,265.58")
      expect(msg).toContain("$10,632.36")
      expect(msg).toContain("$42,387.32")
      expect(msg).toContain("$53,019.68")
      expect(msg).toContain("$3,754.10")
      expect(msg).toContain("the cash and credit memo are $3,754.10 more than the selected invoices")
    })

    // Sugo C&S check 4539695: lines net $11,771.57 (12,380.53 paid − 608.96 deductions) but the
    // amount typed in for the check was $12,130.87, so nothing about the invoices or coding fixes it.
    it("points at the check amount when the check does not foot", () => {
      const msg = build_payment_balance_error({
        selected_invoice_total_cents: 1_264_117,
        check_cash_cents: 1_213_087,
        credit_memo_total_cents: 86_960,
        applied_total_cents: 1_300_047,
        delta_cents: 1_264_117 - 1_300_047,
        remittance_lines_net_cents: 1_177_157,
      })
      expect(msg).toContain("This check does not foot")
      expect(msg).toContain("$12,130.87")
      expect(msg).toContain("$359.30 more than its remittance lines add up to ($11,771.57)")
      expect(msg).not.toContain("Adjust the selected invoices")
    })

    it("says nothing about footing when the check foots", () => {
      expect(build_check_footing_note(1_177_157, 1_177_157)).toBe("")
      expect(build_check_footing_note(1_177_157, undefined)).toBe("")
    })

    it("explains the other direction when invoices fall short", () => {
      const msg = build_payment_balance_error({
        selected_invoice_total_cents: 10_000,
        check_cash_cents: 4_000,
        credit_memo_total_cents: 2_000,
        applied_total_cents: 6_000,
        delta_cents: 4_000,
      })
      expect(msg).toContain("the selected invoices are $40.00 more than the cash and credit memo cover")
    })

    it("names early-pay discounts as a funding source when they ride on the payment", () => {
      // 1st Phorm check 3294787: gross invoices cleared by cash + credit memo + 2% terms discounts.
      const msg = build_payment_balance_error({
        selected_invoice_total_cents: 28_748_246,
        check_cash_cents: 25_957_334,
        credit_memo_total_cents: 2_215_949,
        early_pay_discount_cents: 574_963,
        applied_total_cents: 28_748_246 - 100,
        delta_cents: 100,
      })
      expect(msg).toContain("early-pay discounts taken on the payment ($5,749.63)")
      expect(msg).toContain("the selected invoices are $1.00 more than the cash, credit memo, and discounts cover")
    })
  })

  describe("find_auto_assigned_qb_customer", () => {
    const customers: QbCustomerPick[] = [
      { remote_id: "1", display_name: "UNFI East" },
      { remote_id: "2", display_name: "UNFI West" },
      { remote_id: "3", display_name: "Acme Grocery" },
    ]

    it("auto-selects by remote id from matched accounting payment", () => {
      const match = find_auto_assigned_qb_customer({ customer_remote_id: "2" }, customers)
      expect(match?.remote_id).toBe("2")
    })

    it("auto-selects exact normalized customer name", () => {
      const match = find_auto_assigned_qb_customer({ customer_name_hint: "  unfi east " }, customers)
      expect(match?.remote_id).toBe("1")
    })

    it("auto-selects sole source match for unfi", () => {
      const only_unfi: QbCustomerPick[] = [{ remote_id: "1", display_name: "UNFI East" }]
      const match = find_auto_assigned_qb_customer({ deduction_source: "unfi" }, only_unfi)
      expect(match?.remote_id).toBe("1")
    })

    it("does not auto-select when multiple source matches tie", () => {
      const match = find_auto_assigned_qb_customer({ deduction_source: "unfi" }, customers)
      expect(match).toBeNull()
    })

    it("returns null when no signal is strong enough", () => {
      const match = find_auto_assigned_qb_customer({ customer_name_hint: "Unknown Retailer" }, customers)
      expect(match).toBeNull()
    })
  })
}

/** Everything that still stops the Apply button, in the order a person would fix it. */
export interface ApplyGateInput {
  accounting_system: string
  customer_missing: boolean
  /** One customer per vendor-account group is being chosen (Ahold-style split checks). */
  split_mode: boolean
  unassigned_invoice_count: number
  coding_issue_message: string
  unresolved_prepayments_message: string
  payment_balance_error: string | null
  short_pay_undecided: boolean
  discount_undecided: boolean
  overpaid_error: string | null
  gl_code_issues_message: string
  repayment_invoice_unsupported: boolean
}

/**
 * The reasons Apply is disabled, as sentences. Empty means the check can be applied. The button
 * used to be disabled on the same conditions with no message, because the explanations lived in
 * the click handler a disabled button never runs.
 */
export function apply_blockers(input: ApplyGateInput): string[] {
  const out: string[] = []
  if (input.customer_missing) {
    out.push(
      input.split_mode
        ? `Select a ${input.accounting_system} customer for every vendor account.`
        : `Select the ${input.accounting_system} customer this check pays.`
    )
  }
  if (input.unassigned_invoice_count > 0) {
    out.push(
      `Match ${input.unassigned_invoice_count === 1 ? "the unassigned invoice line" : `the ${input.unassigned_invoice_count} unassigned invoice lines`} to invoices in ${input.accounting_system}.`
    )
  }
  if (input.coding_issue_message) out.push(input.coding_issue_message)
  if (input.unresolved_prepayments_message) out.push(input.unresolved_prepayments_message)
  if (input.gl_code_issues_message) out.push(input.gl_code_issues_message)
  if (input.payment_balance_error) out.push(input.payment_balance_error)
  if (input.overpaid_error) out.push(input.overpaid_error)
  if (input.short_pay_undecided) out.push("Choose how to handle the short pay (accept it, or book it as a deduction to dispute).")
  if (input.discount_undecided) out.push("Confirm the additional-discount checkbox, or adjust the coding so the sides balance.")
  if (input.repayment_invoice_unsupported) {
    out.push(`This check nets to a repayment, which ${input.accounting_system} has no way to post from here.`)
  }
  return out
}
